// EvaluationDashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Button, ButtonGroup, Collapse } from "reactstrap";
import Loader from "../../../components/Loader";
import Modal from "react-modal";
import "./Models.scss";
import "../data/Dataset.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faPause, faPlay, faSearch, faSpinner, faStop, faStopCircle, faTimes, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import Deploy from "./Deploy/Deploy";
import useFetch from "../../../components/useFetch";
import Confirm from "../../../components/confirm/Confirm";
import getDataFromApi from "../../../components/getDataFromApi";
import formatOptionLabel from "../../../components/formatLabelOptions";
import "split-pane-react/esm/themes/default.css";
import ModalComponent from "../../../components/modal/ModalComponent";
import DateParse from "../../../components/DateParse";
import Plots from "../../../components/modal/NavPanes/Plots";
import Logs from "../../../components/modal/NavPanes/Logs";
import AiInsights from "../../../components/modal/NavPanes/AiInsights";
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { getCokkie } from "../../login/Login";
import CvEvaluationField from "./CvEvaluationField";
import { Table } from "../../../components/datatable/DataTable";
import Samplepredictionstream from "./Samplepredictionstream";
import EvaluationTablestream from "./EvaluationTablestream";
import { LLM_DRAFT_SESSION_ID } from "./LlmEvaluationField.jsx";
import { AccuracyIcon, AccuracyIcon2, AddDatasetIcon, AiInsightsIcon, AnalyticsIcon, BackIcon, ChatIcon, ChipsetIcon, CloseIcon, CodeIcon, CustomisePlanIcon, DatasetIcon, DeleteIcon, Evaluation, EyeIcon, HistoryIcon, ImagesIcon, LeaderboardIcon, ModelIcon, PlatformIcon, QueueChipIcon, RoundedClockIcon, RunningIcon, TableIcon, TokensPerSecondIcon, UploadIcon, VideoIcon } from "../../Common/SvgIcons/WorkflowIcons";
import { TimeFormatter } from "../../../utils/TimeFormatter";
import EvaluationProgressStream from "./EvaluationProgressStream";
import LlmEvaluationField, { closeLlmEvaluationStream } from "./LlmEvaluationField.jsx";
import EvaluationAnalytics from "../../hub/EvaluationAnalytics";
import { loadInfinity } from "../../../useInfinityLoader";
import PathBreadcrumb from "../../../components/PathBreadcrumb/PathBreadcrumb";

// Variables for filtering data
let selectedFilterClass = [],
  selectedFilterCompatibility = [];

const EVALUATION_HISTORY_PAGE_INCREMENT = 16;

const createEmptyLlmSession = () => ({
  history: [],
  chatImages: [],
  selectedImages: [],
  pendingPrompt: "",
  loading: false,
  firstEvaluation: true,
  sse: null,
  responseStreaming: false,
  streamWait: false,
  page: 1,
  totalPages: 1,
  activeContext: 1,
  availableContexts: [1],
  contextChatSummaries: {},
  contextHistories: {},
  contextPagination: {},
  streamingContext: 1,
  pendingPredictionsByEvalId: {},
});

const getEvaluationHistoryStatusIcon = (status) => {
  const normalizedStatus = String(status || "").trim().toLowerCase();

  if (["complete", "completed"].includes(normalizedStatus)) {
    return { icon: faCheckCircle, color: "#22c55e", label: "Complete" };
  }

  if (["pause", "paused"].includes(normalizedStatus)) {
    return { icon: faPause, color: "#ef4444", label: "Paused" };
  }

  if (["stop", "stopped", "terminate", "terminated"].includes(normalizedStatus)) {
    return { icon: faStopCircle, color: "#ef4444", label: "Terminated" };
  }

  if (["fail", "failed", "failure"].includes(normalizedStatus)) {
    return { icon: faTriangleExclamation, color: "#ef4444", label: "Failed" };
  }

  if (["evaluating", "starting", "converting"].includes(normalizedStatus)) {
    return { icon: faSpinner, color: "#3b82f6", label: "Evaluating", spin: true };
  }

  return null;
};

const normalizeEvaluationHistoryData = (historyData) => {
  if (!historyData) return historyData;

  const historyRows = Array.isArray(historyData?.device_table?.data)
    ? historyData.device_table.data
    : [];
  const existingOptions = Array.isArray(historyData?.eval_ids_names)
    ? historyData.eval_ids_names
    : [];

  // The lightweight history stream now returns only name, status and eval_id.
  // Keep the existing dashboard contract until evaluation details are fetched
  // separately when a history item is selected.
  const evalIdsNames = existingOptions.length > 0
    ? existingOptions
    : historyRows
        .filter((row) => row?.eval_id)
        .map((row) => ({
          label: row?.name || row.eval_id,
          value: row.eval_id,
        }));

  return {
    ...historyData,
    eval_ids_names: evalIdsNames,
  };
};

const getEvaluationHistoryDetailRow = (payload, evaluationId) => {
  const candidates = [
    payload?.device_table?.data,
    payload?.evaluation_history_details?.data,
    payload?.evaluation_history_details,
    payload?.evaluation_details?.data,
    payload?.evaluation_details,
    payload?.detail,
    payload?.data,
    payload,
  ];

  for (const candidate of candidates) {
    const rows = Array.isArray(candidate) ? candidate : [candidate];
    const validRows = rows.filter(
      (row) => row && typeof row === "object" && !Array.isArray(row)
    );
    if (validRows.length === 0) continue;

    const matchingRow = validRows.find((row) =>
      [row?.eval_id, row?.evaluation_id, row?._id].includes(evaluationId)
    );
    if (matchingRow) return matchingRow;

    // The details endpoint is scoped to one evaluation, so a single row does
    // not need to repeat the evaluation id in its response.
    if (validRows.length === 1) return validRows[0];
  }

  return null;
};

const getCvSampleImages = (cloudFrontEndpoint, modelName, taskType) => {
  const endpoint = String(cloudFrontEndpoint || "").trim().replace(/\/+$/, "");
  const normalizedModelName = String(modelName || "").trim();

  if (!endpoint || !normalizedModelName) return [];

  const createSamples = (imageType = "") =>
    Array.from({ length: 3 }, (_, index) => {
      const imageNumber = index + 1;
      const imageName = imageType
        ? `${normalizedModelName}_${imageType}_${imageNumber}.jpg`
        : `${normalizedModelName}_${imageNumber}.jpg`;
      const imageDirectory = imageType
        ? `${normalizedModelName}/${imageType}`
        : normalizedModelName;

      return {
        src: `${endpoint}/sample_examples/${imageDirectory}/${imageName}`,
        name: imageName,
        label: imageType
          ? `${imageType === "reference" ? "Reference" : "Test"} sample ${imageNumber}`
          : `${normalizedModelName} sample ${imageNumber}`,
        target: imageType || "test",
      };
    });

  return taskType === "FaceRecognition"
    ? [...createSamples("reference"), ...createSamples("test")]
    : createSamples();
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);

    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
}

function EvaluationSummaryStrip({ panels, evaluationId }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const isStackedView = useMediaQuery("(max-width: 992px)");

  useEffect(() => {
    setActiveIndex(0);
  }, [evaluationId]);

  if (!panels.length) return null;

  return (
    <div className={`evaluation-summary-strip ${isStackedView ? "evaluation-summary-strip--stacked" : "evaluation-summary-strip--interactive"}`}>
      {panels.map((panel, index) => {
        const isExpanded = isStackedView || index === activeIndex;
        const activate = () => {
          if (!isStackedView) setActiveIndex(index);
        };

        return (
          <article
            key={panel.id}
            className={`evaluation-summary-panel evaluation-summary-panel--${panel.id} tone-${panel.tone}${isExpanded ? " is-expanded" : " is-collapsed"}`}
            onMouseEnter={activate}
            onFocus={activate}
            onClick={activate}
            onKeyDown={(event) => {
              if (!isStackedView && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                activate();
              }
            }}
            role={isStackedView ? undefined : "button"}
            tabIndex={isStackedView ? -1 : 0}
            aria-expanded={isExpanded}
            aria-label={panel.label}
          >
            <div className="evaluation-summary-panel__collapsed" aria-hidden={isExpanded}>
              <span>{panel.shortLabel || panel.label}</span>
              <strong>{panel.shortValue}</strong>
              <i>{panel.icon}</i>
            </div>
            <div className="evaluation-summary-panel__expanded" aria-hidden={!isExpanded}>
              <div className="evaluation-summary-panel__head">
                <span>{panel.label}</span>
                <i>{panel.icon}</i>
              </div>
              <h3>{panel.title}</h3>
              {panel.description && <p>{panel.description}</p>}
              <dl>
                {panel.details.map((detail) => (
                  <div key={detail.label}>
                    <dt
                      title={
                        typeof detail.label === "string" || typeof detail.label === "number"
                          ? String(detail.label)
                          : undefined
                      }
                    >
                      {detail.label}
                    </dt>
                    <dd
                      title={
                        typeof detail.value === "string" || typeof detail.value === "number"
                          ? String(detail.value)
                          : undefined
                      }
                    >
                      {detail.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function EvaluationDashboard({ id }) {


  const customStyles2 = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "transparent",
      width: "200px ",
      border: "none",
      padding: "0px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "var(--bg-color8)",
      border: "none",
      zIndex: "9999",
      padding: "0px",
    }),
  };
  const MODEL_NAME_MAP = {
    "resnet50": "Resnet50",
    "resnet": "Resnet50",
    "yolox": "yolox",
    "yoloxs": "yolox",
    "topformer": "topformer",
    "topformerb": "topformer",
    "retinaface": "retinaface",
    "retinafaceresnet50": "retinaface",
    "longclip": "longclip",
    "longclipb16": "longclip",
    "facenet": "facenet",
    "facenetmobilenetv1": "facenet",
    "llavaonevision7b": "llava-OneVision-7b",
    "llavaonevisionqwen2": "llava-OneVision-7b",
    "llava-onevision-7b": "llava-OneVision-7b",
    "llava-onevision-qwen2-7b": "llava-OneVision-qwen2-7b",
    "swinv2tiny": "swinv2-tiny",
    "swinv2t": "swinv2-tiny",
    "convnext": "convnext",
    "convnextt": "convnext",
    "deeplabv3plus": "deeplabv3plus",
    "deeplabv3plusresnet50": "deeplabv3plus",
    "efficientnetv2": "efficientnetv2",
    "efficientnetv2s": "efficientnetv2",
    "mobilenetv2": "mobilenetv2",
    "convmixer": "convmixer",
    "convmixer76832": "convmixer",
    "depthanythingv2": "depthanythingv2",
    "depthanythingv2small": "depthanythingv2",
    "rtmpose": "rtmpose",
    "rtmposem": "rtmpose",
    "efficientvitl2": "efficientvit-l2",
    "owlvitb32": "owlvit",
    "owlvit": "owlvit",
    "deepseekr1distillqwen7B": "DeepSeek-R1-Distill-Qwen-7B",
    "internvl351b": "InternVL-3.5-1B",
    "florence2": "florence2",
    "rtmdetnano": "RTMDet",
    "superpoint": "superpoint"



  };

  const Tas_type_map = {
    "Object Detection": "Object Detection",
    "Classification": "Image Classification",
    "Segmentation": "Semantic Segmentation",
    "Pose Estimation": "Pose Estimation",
    "Face Landmark Detection": "Face Recognition & Face Detection",
    "Face Recognition": "Face Recognition & Face Detection",
    "Depth Estimation": "Depth Estimation",
    "Keypoint Detection": "Keypoint Detection",
    "Text Classification": "",
    "Text Summarization": "",
    "Language Translation": "",
    "Question Answering": "",
    "Text Generation": "",
    "Text Embeddings Generation": "",
    "Text Similarity": "",
    "Audio Classification": "",
    "Speech Recognition": "",
    "Voice Activity Detection": "",
    "Text-to-Speech": "",
    "Speech-to-Text": "",
    "Visual Question Answering": "Vision-Language",
    "Video Captioning": "",
    "Image Captioning": "",
    "Image-Text Retrieval": "",
    "Visual-Text Matching": "",
    "ZeroShot Detection": "Object Detection",
    "ZeroShot Classification": "",
    "Image-to-Video": "",
    "Text Generation": "",
    "Text Embeddings Generation": "",
    "Text Similarity": "",
    "Audio-Text-to-Text": "",
    "Video-Text-to-Text": "",
    "Image-Text-to-Text": ""
  };



  function normalizeModelName(input) {
    if (!input) return null;

    const key = input.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    return MODEL_NAME_MAP[key] || input;
  }


  const location = useLocation();
  const { onPathDetailChange } = useOutletContext() || {};
  const { architecture: routeArchitecture } = useParams();
  const [searchParams] = useSearchParams();
  const fromProjects =
    location?.state?.through_project ||
    location.pathname.startsWith("/projects/");
  const isAmbarellaCustomer = window.ENV?.CUSTOMER === "ambarella";
  const projectId = fromProjects ? location.pathname.split("/")[2] : null;
  const showEvaluationBreadcrumb = !fromProjects && !isAmbarellaCustomer;

  useEffect(() => {
    if (location.pathname.startsWith("/hub/")) {
      loadInfinity();
    }
  }, [location.pathname]);

  const model_Name = searchParams.get("model_name");
  const chipset = searchParams.get("chipset")?.toLowerCase();
  const compression = searchParams.get("compression");
  const pathModelNameRaw = window.location.pathname.split("/")[2] || "";
  let pathModelName = pathModelNameRaw;
  try {
    pathModelName = decodeURIComponent(pathModelNameRaw);
  } catch {
    pathModelName = pathModelNameRaw;
  }
  const mode_id = window.location.pathname.split("/")[4];
  const adminEvaluationId = location?.state?.evalId;
  const viewFrom = location?.state?.viewFrom;
  const selectedCard = location?.state?.selectedCard;
  const modelVersion =
    location?.state?.version ??
    selectedCard?.version ??
    searchParams.get("version") ??
    null;

  const finalModelNameRaw = model_Name || pathModelName;
  const finalModelName = normalizeModelName(finalModelNameRaw);

  const buildEvaluationUrl = () => {
    if (fromProjects) {
      const name = selectedCard?.name || finalModelName;
      const id = selectedCard?._id || mode_id;
      if (name && modelVersion != null && modelVersion !== "") {
        return `models?filters=${JSON.stringify({
          name,
          version: modelVersion,
        })}&section=evaluation`;
      }
      if (id) {
        return `models?filters=${JSON.stringify({ _id: id })}&section=evaluation`;
      }
    }
    return `models?filters=${JSON.stringify({
      name: finalModelName,
    })}&section=evaluation`;
  };

  let url = buildEvaluationUrl();
  const displayModelName =
    selectedCard?.name || finalModelNameRaw || null;

  const privacy_policy_accepted = localStorage.getItem("privacy_policy_accepted");
  const [openModal, setOpenModal] = useState();
  const [deleteUrl, setDeleteUrl] = useState();
  const [evaluationSelect, setEvaluationSelect] = useState([]);
  const [pendingCreatedEvaluationId, setPendingCreatedEvaluationId] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [childData, setChildData] = useState(null);
  const [callStart, setCallStart] = useState(false);
  const [callStartTerminate, setCallStartTerminate] = useState(false);
  const [collapse, setCollapse] = useState(true);
  const [load, setLoading] = useState(false);
  const [message, setmessage] = useState("");
  const [messagesByEvalId, setMessagesByEvalId] = useState({ evalId: null, status: "", dataset: '' });
  const [model, setModel] = useState();
  const [compatibilitydata, setCompatibility] = useState(null);
  const [summary, setSummary] = useState(null);
  const [compatibilityfilters, setCompatibilityfilters] = useState({});
  const [Compatibilytfilterfamily, setCompatibilityfilterfamily] = useState();
  const [Compatibilytfilterchipset, setCompatibilityfilterchipset] = useState();
  const [summaryfilter, setSummaryfilter] = useState(null);
  const [chatBox, setChatBox] = useState(false);
  const [chatBoxPrompt, setChatBoxPrompt] = useState("");
  const [chatBoxPromptid, setChatBoxPromptid] = useState("");
  const [selectedLatestDevice, setSelectedLatestDevice] = useState("");
  const [selectedLatestChipset, setSelectedLatestChipset] = useState("");
  const [streamData, setStreamData] = useState({ annotations: {} });
  const rendertextfieldref = useRef();
  const [targethw, settargethw] = useState('');
  const [imageslength, setimageslength] = useState();
  const [devicestatus, setdevicestatus] = useState();
  const [isPaused, setIsPaused] = useState(false);
  const [isTeriminated, setIsTerminated] = useState(false);
  const [isdeviceselected, setIsdeviceselected] = useState('');
  const [chatImag, setchatImg] = useState([]);
  const [totalImages, setTotalImages] = useState({});
  const [totalImages2, setTotalImages2] = useState([]);
  const [pageChange, setpageChange] = useState(null);
  const [evaluationhistorylength, setEvaluationHistoryLength] = useState(0)
  const [sessions, setSessions] = useState({})
  const [openEvalDrawer, setOpenEvalDrawer] = useState(false);
  const [initialEvaluationSource, setInitialEvaluationSource] = useState("");
  const [selectedCvSampleImages, setSelectedCvSampleImages] = useState([]);
  const [showLayerModal, setShowLayerModal] = useState(false);
  const [layerModalType, setLayerModalType] = useState(null);
  const [showModelInfoModal, setShowModelInfoModal] = useState(false);
  const [loadButton, setLoadButton] = useState(false)
  const [deletingEvalId, setDeletingEvalId] = useState(null);
  const deletingEvalIdRef = useRef(null);
  const [historySearch, setHistorySearch] = useState("");
  const [showHistorySearch, setShowHistorySearch] = useState(false);
  const [latestDeviceStatus, setLatestDeviceStatus] = useState({});
  const [busyPromptByEvalId, setBusyPromptByEvalId] = useState({});
  const [showLogsPane, setShowLogsPane] = useState(false);
  const [showLlmMetricsPanel, setShowLlmMetricsPanel] = useState(false);
  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [showFreshAnalytics, setShowFreshAnalytics] = useState(false);
  const [benchmarkNotice, setBenchmarkNotice] = useState("");
  const [evaluationDetailsById, setEvaluationDetailsById] = useState({});
  const [evaluationHistoryPageSize, setEvaluationHistoryPageSize] = useState(
    EVALUATION_HISTORY_PAGE_INCREMENT
  );
  const [isLoadingMoreEvaluations, setIsLoadingMoreEvaluations] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCompactLayout, setIsCompactLayout] = useState(window.innerWidth < 1024);
  const [activeEvaluationOption, setActiveEvaluationOption] = useState("Local PC");
  const [silentRefetching, setSilentRefetching] = useState(false);
  const [showTab, setShowTab] = useState('preview')



  const prevMessageRef = useRef("");
  const evaluationCacheRef = useRef({});
  const resultsPageRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const childRef = useRef();
  const prevEvaluationSelect = useRef(null);
  const prevEvalIdsRef = useRef([]);
  const prevEvalCountRef = useRef(null);
  const refetchTriggeredRef = useRef(false);
  const evaluationDetailsWorkerRef = useRef(null);
  const [completedCountByEvalId, setCompletedCountByEvalId] = useState({});




  const navigate = useNavigate();

  const historyRefs = useRef({});
  const historyListRef = useRef(null);


  const { refetch, loading, data, error } = useFetch(url);

  const hasError = !!error;
  const noSearchResults = false;



  const resetEvaluationResultsScroll = useCallback(() => {
    setShowTab("preview");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        resultsPageRef.current?.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
        scrollContainerRef.current?.scrollTo({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      });
    });
  }, []);

  const handleEvaluationDetailWheel = useCallback((event) => {
    const resultsPage = resultsPageRef.current;
    const detailPanel = event.currentTarget;
    if (!resultsPage || !detailPanel || event.deltaY === 0) return;

    const outerMaxScroll = resultsPage.scrollHeight - resultsPage.clientHeight;
    const outerHasDownRoom = resultsPage.scrollTop < outerMaxScroll - 1;

    if (event.deltaY > 0 && outerHasDownRoom) {
      event.preventDefault();
      resultsPage.scrollTop = Math.min(
        outerMaxScroll,
        resultsPage.scrollTop + event.deltaY
      );
      return;
    }

    if (event.deltaY >= 0 || resultsPage.scrollTop <= 0) return;

    let scrollNode = event.target;
    let innerHasUpRoom = false;

    while (scrollNode && scrollNode !== detailPanel) {
      if (
        scrollNode instanceof HTMLElement &&
        scrollNode.scrollHeight > scrollNode.clientHeight + 1 &&
        scrollNode.scrollTop > 0
      ) {
        innerHasUpRoom = true;
        break;
      }
      scrollNode = scrollNode.parentElement;
    }

    if (!innerHasUpRoom && detailPanel.scrollTop <= 0) {
      event.preventDefault();
      resultsPage.scrollTop = Math.max(0, resultsPage.scrollTop + event.deltaY);
    }
  }, []);



  useEffect(() => { if (messagesByEvalId?.status === "Paused" || messagesByEvalId?.status === "Evaluating") { setCallStart(false) } }, [messagesByEvalId?.status]);

  function useSmoothTimer({ startTime, endTime, status, queuedMode = "countdown" }) {
    const [displayTime, setDisplayTime] = useState(null);

    useEffect(() => {
      if (!status) return;

      const parseUTC = (timeString) => {
        if (!timeString || timeString === "NA") return null;
        return new Date(
          timeString.replace(" ", "T").split(".")[0] + "Z"
        ).getTime();
      };

      const start = parseUTC(startTime);
      const end = parseUTC(endTime);

      const update = () => {
        const now = Date.now();

        // 🟢 Evaluating → count up
        if ((status === "Evaluating" || status === "Paused" || status === "Starting" || status === "Queued") && start && !(status === "Queued" && queuedMode === "countdown")) {
          const elapsed = Math.floor((now - start) / 1000);
          setDisplayTime(elapsed > 0 ? elapsed : 0);
        }

        // 🟡 Queued → countdown
        if (status === "Queued" && queuedMode === "countdown" && end) {
          const diff = (end - now) / 1000;

          if (diff <= 1) {
            setDisplayTime("ANY_TIME");
          } else {
            setDisplayTime(Math.ceil(diff)); // 🔥 use ceil
          }
        }
      };

      update();

      if (status === "Evaluating" || status === "Queued" || status === "Paused" || status === "Starting") {
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
      }

    }, [startTime, endTime, status]);

    return displayTime;
  }



  const InnerMetrics = [
    // {
    //   key: 'map',
    //   title1: (row) => Object.keys(row?.metric)[0] ? Object.keys(row?.metric)[0] :'mAP',
    //   titleLLM: 'ACCURACY',
    //   icon: <AccuracyIcon2 className="wf-icon-blue" />,
    //   value: (row) => row?.metric?.mAP ?? 'N/A',
    // },
    {
      key: "map",

      title1: (row) => {
        if (!row?.metric || row.metric === "NA") {
          return "METRIC";
        }

        const keys = Object.keys(row.metric);
        if (keys.length === 0) return "METRIC";

        const firstKey = keys[0];
        return firstKey.toLowerCase() === "map" ? "mAP" : firstKey.toUpperCase();
      },

      titleLLM: "ACCURACY",
      icon: <AccuracyIcon2 className="border-0 wf-icon-blue" />,

      value: (row) => {
        if (!row?.metric || row.metric === "NA") {
          return "_";
        }

        const keys = Object.keys(row.metric);
        if (keys.length === 0) return "_";

        const firstKey = keys[0];
        const val = row.metric[firstKey];

        return val === "NA" || val == null ? "_" : val;
      },
    },

    {
      key: 'fps',
      title1: (row) => 'FRAME RATE (FPS)',
      titleLLM: 'EMBEDDING GENERATION TIME',
      icon: (isLLM) => isLLM ? <RoundedClockIcon className="border-0 wf-icon-purple" /> : <HistoryIcon className="border-0 wf-icon-yellow" />,
      value: (row, isLLM) => isLLM ? `${row.embedding_generation_time ?? '—'} ms` : row.fps != null ? Math.floor(1000 / Number(row.fps)) : '—',
    },
    {
      key: 'latency',
      title1: (row) => 'AVERAGE LATENCY',
      titleLLM: 'TTFT',
      icon: <RoundedClockIcon className="border-0 wf-icon-purple" />,
      value: (row, isLLM) => isLLM ? `${row.ttft ?? '--'} ms` : row.fps ? `${row.fps} ms` : '—',
    },
    {
      key: 'error_rate',
      title1: (row) => 'MEMORY BANDWIDTH (per frame)',
      titleLLM: 'TOKENS PER SECOND',
      icon: <TokensPerSecondIcon className="border-0 wf-icon-red" />,
      value: (row, isLLM) => isLLM ? row.fps ?? '—' : row.memory_bandwidth != null ? `${row.memory_bandwidth}` : '—',
    },
  ];



  const LLM_TASK_TYPES = ["text generation", "zero shot classification", "zeroshot classification", "visual question answering", "zeroshot detection", "image-text-to-text"];
  const isLLMTaskType = LLM_TASK_TYPES.includes((data?.info?.task_type || "").toLowerCase());
  const selectedEvaluationIdForScroll = evaluationSelect?.[0]?.value || "";
  const hasLlmResultsView =
    isLLMTaskType &&
    Array.isArray(evaluationSelect) &&
    evaluationSelect.length > 0;
  const evaluationLayoutHeight = "calc(100dvh - 72px)";
  const leftPanelStickyTop = !fromProjects ? "80px" : "53px";

  useEffect(() => {
    const detailPanel = scrollContainerRef.current;
    if (!detailPanel || !selectedEvaluationIdForScroll || !isLLMTaskType) return undefined;

    const handleWheel = (event) => handleEvaluationDetailWheel(event);
    detailPanel.addEventListener("wheel", handleWheel, { passive: false });

    return () => detailPanel.removeEventListener("wheel", handleWheel);
  }, [handleEvaluationDetailWheel, isLLMTaskType, selectedEvaluationIdForScroll, showLogsPane, showTab]);


  const evaluationId = messagesByEvalId?.evalId;
  const cachedEval = evaluationCacheRef.current[evaluationId] || {};


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsCompactLayout(window.innerWidth < 1024);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const bestEvalNames = new Set(Object?.values(childData?.evaluation_comparision || {}).map((item) => item[0]));



  useEffect(() => {
    if (!evaluationSelect?.[0]?.value) return;
    resetEvaluationResultsScroll();
    setShowLogsPane(false);
    setShowLlmMetricsPanel(false);
  }, [evaluationSelect?.[0]?.value, messagesByEvalId?.evalId, resetEvaluationResultsScroll]);

  useEffect(() => {
    const stored = sessionStorage.getItem("activeEvalsToTerminate");
    if (stored) {
      const evalIds = JSON.parse(stored);

      evalIds.forEach(eval_id => {
        handleActions(isLLMTaskType ? 'STOP' : "Terminate", eval_id);
      });
      sessionStorage.removeItem("activeEvalsToTerminate");
    }
  }, []);


  useEffect(() => {
    if (!data) return;
    const normalizedHistoryData = normalizeEvaluationHistoryData(data);
    const historyLength =
      Number(normalizedHistoryData?.total_evaluations) ||
      normalizedHistoryData?.eval_ids_names?.length ||
      0;
    if (historyLength === 0) return;
    setEvaluationHistoryLength(historyLength)
  }, [data])



  useEffect(() => {
    if (childData?.eval_ids_names?.length === 0) {
      setpageChange(1)
    }
  }, [callStart])



  const currentEvalId = evaluationSelect?.[0]?.value;
  const currentMessage = messagesByEvalId.evalId === currentEvalId ? messagesByEvalId.status : "";


  useEffect(() => {
    if (currentMessage !== "" && currentMessage !== prevMessageRef.current) {
      prevMessageRef.current = currentMessage;
    }
  }, [currentMessage]);

  const MAX_CACHED_SESSIONS = 5;
  const sessionAccessOrderRef = useRef([]);



  const ensureSession = (evalId) => {
    if (!evalId) return;

    setSessions((prev) => {

      sessionAccessOrderRef.current = [
        evalId,
        ...sessionAccessOrderRef.current.filter(id => id !== evalId),
      ];


      if (prev[evalId]) {
        return prev;
      }


      const next = {
        ...prev,
        [evalId]: createEmptyLlmSession()
      };


      if (sessionAccessOrderRef.current.length > MAX_CACHED_SESSIONS) {
        const evictIds = sessionAccessOrderRef.current.slice(MAX_CACHED_SESSIONS);

        evictIds.forEach((id) => {
          const session = next[id];


          session?.history?.forEach(h => {
            h?.local_images?.forEach(img => {
              if (img?.url) URL.revokeObjectURL(img.url);
            });
          });

          delete next[id];
        });

        sessionAccessOrderRef.current =
          sessionAccessOrderRef.current.slice(0, MAX_CACHED_SESSIONS);
      }

      return next;
    });
  };

  const updateLLMSession = useCallback((updates, targetEvalId) => {
    const sessionId = targetEvalId || messagesByEvalId?.evalId || LLM_DRAFT_SESSION_ID;
    if (!sessionId) return;

    setSessions(prev => {
      const current = prev[sessionId] || createEmptyLlmSession();
      const resolved = typeof updates === "function" ? updates(current) : updates;

      if (!resolved || resolved === current) {
        return prev;
      }

      return {
        ...prev,
        [sessionId]: {
          ...current,
          ...resolved
        }
      };
    });
  }, [messagesByEvalId?.evalId]);

  const resetEvaluationWorkspace = useCallback(() => {
    setEvaluationSelect([]);
    setChatBoxPromptid("");
    setChatBoxPrompt("");
    setchatImg([]);
    setLoadButton(false);
    setShowFreshAnalytics(false);
  }, []);

  const handleEvaluationBreadcrumbBack = useCallback(() => {
    if (showFreshAnalytics) {
      setShowFreshAnalytics(false);
      return;
    }
    if (evaluationSelect.length > 0) {
      resetEvaluationWorkspace();
      return;
    }
    if (isAmbarellaCustomer && chipset) {
      window.location.href = "https://www.ambarella.com/developer/model-garden/";
      return;
    }
    if (fromProjects && projectId) {
      navigate(`/projects/${projectId}/deepdeploy`);
      return;
    }
    const modelSlug = encodeURIComponent(
      data?.info?.name || finalModelNameRaw || displayModelName || finalModelName || ""
    );
    if (modelSlug) {
      navigate(`/hub/${modelSlug}`);
      return;
    }
    navigate("/hub");
  }, [
    chipset,
    data?.info?.name,
    displayModelName,
    evaluationSelect.length,
    finalModelName,
    finalModelNameRaw,
    fromProjects,
    isAmbarellaCustomer,
    navigate,
    projectId,
    resetEvaluationWorkspace,
    showFreshAnalytics,
  ]);

  useEffect(() => {
    if (!benchmarkNotice) return undefined;
    const timer = window.setTimeout(() => setBenchmarkNotice(""), 3200);
    return () => window.clearTimeout(timer);
  }, [benchmarkNotice]);



  useEffect(() => {
    if (evaluationSelect.length > 0) {
      const id = evaluationSelect[0].value;
      ensureSession(id);     // IMPORTANT
    }
  }, [evaluationSelect]);





  const closeModal = () => { setOpenModal() };




  const setMessageFor = (evalId, status, dataset) => {
    if (!evalId) return;
    setMessagesByEvalId({ evalId, status, dataset });
  };

  // ------------------------- useEffects for data processing ------------------------- //
  React.useEffect(() => {
    if (data?.detail) return;
    else {
      if (!data) return;
      try {
        selectedFilterClass = [
          ...new Set(
            data?.summary?.data.map((item) => item?.className).filter(Boolean)
          ),
        ].map((v) => ({ value: v, label: v }));

        selectedFilterCompatibility = [
          ...new Set(
            data?.compatibility?.data
              .map((item) => item?.incompatibleDevices)
              .filter(Boolean)
          ),
        ].map((v) => ({ value: v, label: v }));
        // console.log(selectedFilterClass);
      } catch (error) {
        // console.log(error);
      }
    }
    modelCompatibility();
    modelSummary();
  }, [data]);

  const modelCompatibility = async (family, chipset) => {
    try {
      let url;

      if (family && chipset) {
        url = `${window["ENV"].API_URL}/models?filters={"_id":"${data?.info?._id}","family":"${family.value}","chipset":"${chipset}"}&section=compatibility`;
      } else {
        url = `${window["ENV"].API_URL}/models?filters={"_id":"${data?.info?._id}"}&section=compatibility`;
      }

      const results = await getDataFromApi(url, "get");

      if (!results) {
        console.error("❌ modelCompatibility: API returned no data:", results);
        return;
      }

      setCompatibility(results?.compatibility || []);
      setCompatibilityfilters(results?.filters || []);
    }
    catch (err) {
      console.error("❌ modelCompatibility Error:", err);
    }
  };


  const modelSummary = async (filter) => {
    try {
      let url;

      if (filter) {
        url = `${window["ENV"].API_URL}/models?filters={"_id":"${data?.info?._id}","operations":"${filter.value}"}&section=summary`;
      } else {
        url = `${window["ENV"].API_URL}/models?filters={"_id":"${data?.info?._id}"}&section=summary`;
      }

      const results = await getDataFromApi(url, "get");

      if (!results) {
        console.error("❌ modelSummary: API returned no data:", results);
        return;
      }

      setSummary(results || {});
    }
    catch (err) {
      console.error("❌ modelSummary Error:", err);
    }
  };


  let filterOptions;



  if (window.ENV.CUSTOMER === "ambarella") {
    filterOptions = [
      {
        value: "ambarella",
        label: "Ambarella"
      }
    ];
  } else {
    filterOptions = Object.keys(compatibilityfilters).map((key) => ({
      value: key,
      label: key.charAt(0).toUpperCase() + key.slice(1),
    }));
  }

  useEffect(() => {
    if (!Compatibilytfilterfamily && filterOptions?.length > 0) {
      setCompatibilityfilterfamily(filterOptions[0]);
    }
  }, [filterOptions]);



  const statsSource = useMemo(() => {
    const hasEvaluations =
      childData?.eval_ids_names &&
      childData.eval_ids_names.length > 0;

    if (silentRefetching || !hasEvaluations) {
      return data?.user_stats;
    }

    // Otherwise prefer live stream stats (if valid)
    if (
      childData?.user_stats &&
      Object.keys(childData.user_stats).length > 0
    ) {
      return childData.user_stats;
    }

    return data?.user_stats;
  }, [
    silentRefetching,
    childData?.user_stats,
    childData?.eval_ids_names?.length,
    data?.user_stats
  ]);

  useEffect(() => { prevEvaluationSelect.current = evaluationSelect }, [evaluationSelect]);

  useEffect(() => {
    const evaluationId = evaluationSelect?.[0]?.value;
    const modelId = data?.info?._id;
    const createdBy = data?.info?.current_user_id;

    if (evaluationDetailsWorkerRef.current) {
      try {
        evaluationDetailsWorkerRef.current.terminate();
      } catch {}
      evaluationDetailsWorkerRef.current = null;
    }

    if (!evaluationId || !modelId) return undefined;

    const token = getCokkie("jwtToken");
    if (!token) {
      console.error("JWT token not found for evaluation history details.");
      return undefined;
    }

    const filters = {
      _id: modelId,
      ...(createdBy ? { created_by: createdBy } : {}),
      eval_id: evaluationId,
    };
    const streamUrl = `${window.ENV.API_URL}/models?filters=${encodeURIComponent(
      JSON.stringify(filters)
    )}&section=evaluation_history_details`;
    const worker = new Worker(
      new URL("../../../worker/sseWorker.js", import.meta.url),
      { type: "module" }
    );
    evaluationDetailsWorkerRef.current = worker;
    worker.postMessage({ streamUrl, token });

    worker.onmessage = (event) => {
      const { type, data: payload, message: workerMessage } = event.data || {};

      if (type === "message") {
        const detailRow = getEvaluationHistoryDetailRow(payload, evaluationId);
        if (!detailRow) return;

        setEvaluationDetailsById((previous) => ({
          ...previous,
          [evaluationId]: {
            ...(previous[evaluationId] || {}),
            ...detailRow,
            eval_id: evaluationId,
          },
        }));
        return;
      }

      if (type === "error") {
        console.error("Evaluation history details stream error:", workerMessage);
      }

      if ((type === "done" || type === "error") && evaluationDetailsWorkerRef.current === worker) {
        try {
          worker.terminate();
        } catch {}
        evaluationDetailsWorkerRef.current = null;
      }
    };

    return () => {
      if (evaluationDetailsWorkerRef.current === worker) {
        try {
          worker.terminate();
        } catch {}
        evaluationDetailsWorkerRef.current = null;
      }
    };
  }, [
    evaluationSelect?.[0]?.value,
    data?.info?._id,
    data?.info?.current_user_id,
  ]);

  // The creation response and the history stream can arrive in either order.
  // Keep the new ID in state so selection is retried whenever either one changes.
  useEffect(() => {
    if (!pendingCreatedEvaluationId) return;

    const createdEvaluation = childData?.eval_ids_names?.find(
      (evaluation) => evaluation.value === pendingCreatedEvaluationId
    );

    if (!createdEvaluation) return;

    setEvaluationSelect((current) =>
      current?.[0]?.value === createdEvaluation.value
        ? current
        : [createdEvaluation]
    );
    setChatBox(true);
    setCallStart(false);
    setPendingCreatedEvaluationId(null);
  }, [pendingCreatedEvaluationId, childData?.eval_ids_names]);


  const handleDataChange = (incomingData, page) => {
    const data = normalizeEvaluationHistoryData(incomingData);
    setChildData(data);
    setLatestDeviceStatus(data?.device_status);
    setEvaluationHistoryLength(
      Number(data?.total_evaluations) || data?.eval_ids_names?.length || 0
    );
    setIsLoadingMoreEvaluations(false);

    if (data?.eval_ids_names?.length > 0 && data?.device_table?.data?.length > 0) {
      const latestEvalId = data.eval_ids_names[0].value;
      const latestRow = data.device_table.data.find(
        row => row.eval_id === latestEvalId
      );

      if (latestRow?.device) {
        const { device, chipset } = parseDevice(latestRow.device);
        setSelectedLatestDevice('');
        setSelectedLatestChipset(device);
      }
    }
    // ---------- compare incoming eval IDs to previously fetched ----------
    const incomingIds = (data?.eval_ids_names ?? [])
      .map(x => x?.value)
      .filter(Boolean);

    const prevIds = prevEvalIdsRef.current ?? [];
    const incomingSorted = [...incomingIds].sort();
    const prevSorted = [...prevIds].sort();
    const sameIds =
      incomingSorted.length === prevSorted.length &&
      incomingSorted.every((id, i) => id === prevSorted[i]);

    if (!sameIds) {
      prevEvalIdsRef.current = incomingIds;

      // ✅ find admin eval if exists
      const adminEval =
        viewFrom === "admin" && adminEvaluationId
          ? data?.eval_ids_names?.find(e => e.value === adminEvaluationId)
          : null;

      if (prevSorted.length > 0 || incomingSorted.length > 0) {
        if (page === 1) {

          // ✅ PRIORITY 1: Admin evaluation
          if (
            adminEval &&
            (!evaluationSelect?.[0] || evaluationSelect[0]?.value !== adminEval.value)
          ) {
            setEvaluationSelect([adminEval]);
            setChatBox(true);
            setCallStart(false);
            return; // ⛔ stop further execution
          }

        }
      }
    } else {
    }

    // ---------- device status update (always runs) ----------
    if (data?.device_table?.data && evaluationSelect?.[0]?.label && evaluationSelect.length > 0 && !callStart) {
      const selectedLabel = evaluationSelect[0].label;

      const matchedDevice = data.device_table.data.find(
        (item) => item.name === selectedLabel || item.model_ === selectedLabel
      );

      if (matchedDevice) {
        setmessage(prev => prev !== matchedDevice.status ? matchedDevice.status : prev);
        setMessageFor(matchedDevice.eval_id ?? currentEvalId, matchedDevice.status, matchedDevice?.dataset);
      } else {
        setmessage(prev =>
          prev !== "No matching device found" ? "No matching device found" : prev
        );
      }
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const activeStatuses = ["Evaluating", "starting", "Converting", "Pause", "Queued"];

      const uploadStateRaw = sessionStorage.getItem("evalUploadState");
      const uploadState = uploadStateRaw ? JSON.parse(uploadStateRaw) : {};

      const activeEvals =
        childData?.device_table?.data?.filter((item) => {
          if (!activeStatuses.includes(item.status)) return false;

          const id = item.eval_id; // make sure this matches what you store (evaluation_id vs eval_id)
          const isUploadComplete = uploadState?.[id]?.complete === true;

          // Only terminate if upload is NOT complete
          return !isUploadComplete;
        }) || [];

      if (activeEvals.length > 0) {
        const ids = activeEvals.map((e) => e.eval_id);
        sessionStorage.setItem("activeEvalsToTerminate", JSON.stringify(ids));

        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [childData]);

  const clearUploadState = (evalId) => {
    const raw = sessionStorage.getItem("evalUploadState");
    if (!raw) return;
    const map = JSON.parse(raw);
    delete map[evalId];
    sessionStorage.setItem("evalUploadState", JSON.stringify(map));
  };


  useEffect(() => {
    const rows = childData?.device_table?.data || [];
    const finalStatuses = ["Completed", "Failed", "Terminated"];

    rows.forEach((r) => {
      if (finalStatuses.includes(r.status)) {
        clearUploadState(r.eval_id);
      }
    });
  }, [childData]);



  useEffect(() => {
    if (evaluationSelect.length > 0) {
      const selectedEvaluation = evaluationSelect[0];

      // If evaluationSelect changes, find the matching device status
      if (childData?.device_table?.data) {
        const matchedDevice = childData.device_table.data.find(
          (item) => item.name === selectedEvaluation?.label
        );
        if (matchedDevice) {
          setIsdeviceselected(matchedDevice?.device);
          setmessage(matchedDevice?.status);
          setMessageFor(matchedDevice?.eval_id ?? selectedEvaluation?.value, matchedDevice?.status, matchedDevice?.dataset);
          if (!["Complete", "Paused", "Terminated"].includes(matchedDevice.status)) {
            setdevicestatus(matchedDevice.status);
            setSelectedTarget(matchedDevice.status);
          }
          setSelectedTarget(matchedDevice.status);
        } else {
          setmessage("No matching device found");
        }
      }
    }
  }, [evaluationSelect, childData]);





  const timeSearchVariants = (seconds) => {
    if (seconds == null || seconds === "NA") return [];

    const formatted = TimeFormatter(seconds);
    if (!formatted || formatted === "--") return [];

    const variants = new Set();

    const lower = formatted.toLowerCase();
    variants.add(lower);                  // "2 min 40 sec"
    variants.add(lower.replace(/\s/g, "")); // "2min40sec"

    // Extract numbers: 2, 40
    const numbers = lower.match(/\d+/g) || [];
    numbers.forEach(n => variants.add(n));

    // Unit aliases
    if (lower.includes("sec")) {
      ["sec", "second", "seconds", "s"].forEach(v => variants.add(v));
    }

    if (lower.includes("min")) {
      ["min", "mins", "minute", "minutes", "m"].forEach(v => variants.add(v));
    }

    if (lower.includes("hr")) {
      ["hr", "hrs", "hour", "hours", "h"].forEach(v => variants.add(v));
    }

    return [...variants];
  };



  const filteredEvalHistory = useMemo(() => {
    if (!childData?.eval_ids_names) return [];

    const query = historySearch.trim().toLowerCase();
    if (!query) return childData.eval_ids_names;

    const tokens = query.split(/\s+/);

    return childData.eval_ids_names.filter((item, index) => {
      const row =
        childData.device_table?.data?.find(r => r.eval_id === item.value) ||
        childData.device_table?.data?.[index];

      if (!row) return false;

      const isBest = bestEvalNames.has(item.label);

      const timeFields =
        row.completed_time != null && row.completed_time !== "NA"
          ? timeSearchVariants(row.completed_time)
          : [
            ...timeSearchVariants(row.estimated_time),
            ...timeSearchVariants(row.wait_time),
          ];

      const fields = [
        item.label,
        row.device,
        row.dataset,
        row.compression,
        row.status,
        row.input_size,
        row.input_tokens,
        row.context_window_size,
        row.fps,
        row.map,
        row.error_rate,
        row.ttft,
        isBest ? "best" : "",
        ...timeFields, // ✅ formatted-time-only
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return tokens.every(token => fields.includes(token));
    });
  }, [historySearch, childData, bestEvalNames]);

  const hasMoreEvaluationHistory = Number(childData?.total_pages || 1) > 1;

  const handleLoadMoreEvaluationHistory = () => {
    if (isLoadingMoreEvaluations || !hasMoreEvaluationHistory) return;
    setIsLoadingMoreEvaluations(true);
    setEvaluationHistoryPageSize(
      (currentSize) => currentSize + EVALUATION_HISTORY_PAGE_INCREMENT
    );
  };



  // ------------- Icon Mapping and Dropdown Handlers ------------- //

  React.useEffect(() => {
    if (data && Compatibilytfilterfamily && Compatibilytfilterchipset) {
      modelCompatibility(Compatibilytfilterfamily, Compatibilytfilterchipset);
    }
  }, [Compatibilytfilterfamily, Compatibilytfilterchipset, data]);

  React.useEffect(() => {
    if (data && summaryfilter !== null) {
      modelSummary(summaryfilter);
    }
  }, [summaryfilter, data]);

  const samUrl = useMemo(() => {
    if (!evaluationSelect[0]?.value) return null;
    return `evaluations?filters={"_id":"${evaluationSelect[0]?.value}"}&section=predictions`;
  }, [evaluationSelect]);

  const EVALUATION_TIPS = [
    {
      title: "What Evaluations Measure",
      description:
        "Evaluations measure accuracy, latency, and hardware performance together."
    },
    {
      title: "Compare Multiple Runs",
      description:
        "Compare multiple runs to find the best trade-off between speed and accuracy."
    },
    {
      title: "Latency Considerations",
      description:
        "Latency metrics depend on device type and batch size."
    },
    {
      title: "Ensure Fair Comparisons",
      description:
        "Use the same dataset to ensure fair model comparisons."
    },
    {
      title: "Track Model Regressions",
      description:
        "Evaluation history helps track regressions across model versions."
    },
    {
      title: "Automatic Scheduling",
      description:
        "Evaluations start automatically once hardware resources become available."
    }
  ];


  const [tipIndex, setTipIndex] = useState(() =>
    Math.floor(Math.random() * EVALUATION_TIPS.length)
  )
  const [tipVisible, setTipVisible] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => {
      setTipVisible(false); // fade out

      setTimeout(() => {
        setTipIndex((prev) => (prev + 1) % EVALUATION_TIPS.length);
        setTipVisible(true); // fade in
      }, 1000); // fade duration
    }, 3000);

    return () => clearInterval(interval);
  }, []);



  const FREE_EVAL_LIMIT = Number(data?.user_limits?.total?.total_evaluations ?? 0);
  const FREE_MEM_LIMIT = Number(data?.user_limits?.total?.memory_usage ?? 0) * 2048;
  const FREE_TOKEN_LIMIT = 999999;
  const NEAR_TOKEN_THRESHOLD = 300;


  const NEAR_EVAL_THRESHOLD = 2;
  const NEAR_MEM_THRESHOLD_MB = 200; // MB

  const totalEvals = Number(statsSource?.total_evaluations ?? 0);
  const memUsedMB = Number(statsSource?.memory_consumed ?? 0) * 1024;
  const tokensUsed = Number(statsSource?.total_tokens ?? 0);

  const evalRemaining = Math.max(0, FREE_EVAL_LIMIT - totalEvals);
  const memRemainingMB = Math.max(0, FREE_MEM_LIMIT - memUsedMB);
  const tokensRemaining = Math.max(0, FREE_TOKEN_LIMIT - tokensUsed);

  // Limits of 0 / missing mean unrestricted — do not treat as "reached"
  const evalLimitReached = FREE_EVAL_LIMIT > 0 && totalEvals >= FREE_EVAL_LIMIT;
  const memLimitReached = FREE_MEM_LIMIT > 0 && memUsedMB >= FREE_MEM_LIMIT;
  const tokenLimitReached = FREE_TOKEN_LIMIT > 0 && tokensUsed >= FREE_TOKEN_LIMIT;

  const tokensRemainingFormatted = `${Math.round(tokensRemaining)} tokens`;

  const limitReached = evalLimitReached || memLimitReached || tokenLimitReached;

  const showEvalWarning =
    !evalLimitReached && evalRemaining > 0 && evalRemaining <= NEAR_EVAL_THRESHOLD;

  const showMemWarning =
    !memLimitReached && memRemainingMB > 0 && memRemainingMB <= NEAR_MEM_THRESHOLD_MB;

  const showTokenWarning =
    !tokenLimitReached && tokensRemaining > 0 && tokensRemaining <= NEAR_TOKEN_THRESHOLD;

  const memRemainingFormatted = `${Math.round(memRemainingMB)} MB`;

  const handleStartEvaluation = () => {
    setInitialEvaluationSource("image");
    setSelectedCvSampleImages([]);
    setOpenEvalDrawer(true);
    setCollapse(true);
    setCallStart(false);
  };

  const handleNewConversation = () => {
    setEvaluationSelect([]);
    setShowFreshAnalytics(false);
    setChatBox(false);
    setChatBoxPromptid("");
    setChatBoxPrompt("");
    setchatImg([]);
    setLoadButton(false);
    setShowLogsPane(false);
    setShowLlmMetricsPanel(false);
    setCollapse(true);
    if (isCompactLayout) setShowLeftPanel(false);
  };

  const handleEvaluationSourceSelect = (source) => {
    setInitialEvaluationSource(source);
    setSelectedCvSampleImages([]);

    if (source === "image" || source === "s3" || source === "stream") {
      setOpenEvalDrawer(true);
      setCollapse(true);
      setCallStart(false);
    }
  };

  const handleCvSampleSelect = (sample) => {
    setInitialEvaluationSource("image");
    setSelectedCvSampleImages((current) => {
      const sampleKey = `${sample.target}:${sample.name}`;
      const isSelected = current.some(
        (item) => `${item.target}:${item.name}` === sampleKey
      );

      return isSelected
        ? current.filter((item) => `${item.target}:${item.name}` !== sampleKey)
        : [...current, sample];
    });
  };

  const handleCvEvaluationGo = () => {
    if (!selectedCvSampleImages.length) return;
    setInitialEvaluationSource("image");
    setOpenEvalDrawer(true);
    setCollapse(true);
    setCallStart(false);
  };

  const evaluationSourceConfig = {
    image: {
      title: "Local PC",
      description: "Upload images or ZIP files",
      icon: <UploadIcon className="wf-icon" />,
    },
    s3: {
      title: "Cloud Transfer (S3)",
      description: "Connect an AWS S3 bucket",
      icon: <AddDatasetIcon className="wf-icon" />,
    },
    dataset: {
      title: "Platform Import",
      description: "Import data already on DeepEdge",
      icon: <PlatformIcon className="wf-icon" />,
      disabled: true,
    },
    stream: {
      title: "Live Stream",
      description: "Evaluate a live camera or video stream",
      icon: <VideoIcon className="wf-icon" />,
      // disabled: true,
    },
    "image-text": {
      title: "Image + Text",
      description: "Upload visual inputs with a text prompt",
      icon: <ImagesIcon className="wf-icon" />,
    },
  };

  const evaluationSources = (data?.guidelines?.data_input_format || [])
    .filter((source) => evaluationSourceConfig[source])
    .map((source) => ({ source, ...evaluationSourceConfig[source] }));

  const cvSampleImages = getCvSampleImages(
    data?.cloudfront_details?.aws_cf_endpoint,
    data?.info?.name,
    data?.info?.task_type
  );

  const handleUpgrade = () => {
    // navigate("/billing");
    // or setOpenUpgradeModal(true);
  };

  useEffect(() => {
    const currentCount = childData?.eval_ids_names?.length;

    if (prevEvalCountRef.current === null) {
      prevEvalCountRef.current = currentCount;
      return;
    }

    const prevCount = prevEvalCountRef.current;

    if (
      prevCount === 1 &&
      currentCount === 0 &&
      !refetchTriggeredRef.current
    ) {
      refetchTriggeredRef.current = true;

      // 🔥 ADD THESE TWO LINES
      setSilentRefetching(true);
      setTimeout(() => {
        refetch();
      }, 1000);

      setTimeout(() => {
        refetchTriggeredRef.current = false;
      }, 1000);
    }

    prevEvalCountRef.current = currentCount;
  }, [childData?.eval_ids_names?.length, refetch]);

  const selectedEval = evaluationSelect[0];
  const selectedHistoryRow =
    childData?.device_table?.data?.find(r => r?.eval_id === selectedEval?.value) || {};
  const selectedRow = selectedEval?.value
    ? {
        ...(evaluationDetailsById[selectedEval.value] || {}),
        ...selectedHistoryRow,
      }
    : {};
  const selectedEvaluationStatus =
    messagesByEvalId?.evalId === selectedEval?.value && messagesByEvalId?.status
      ? messagesByEvalId.status
      : selectedRow.status;
  const isSelectedEvaluationComplete = ["complete", "completed"].includes(
    String(selectedEvaluationStatus || "").trim().toLowerCase()
  );

  useEffect(() => {
    if (showTab === "insights" && !isSelectedEvaluationComplete) {
      setShowTab("preview");
    }
  }, [isSelectedEvaluationComplete, showTab]);

  // capabilities from backend
  const canTerminate = !!selectedRow.enable_terminate;

  // pause/resume depend on current status + flags
  const canPauseBackend = !!selectedRow.enable_pause;
  const canResumeBackend = !!selectedRow.enable_resume;

  const isPausedStatus = (messagesByEvalId?.status || selectedRow.status) === "Paused";
  const canPause = !isPausedStatus && canPauseBackend;
  const canResume = isPausedStatus && canResumeBackend;

  // If you also want your “processedImages >= 1” rule, combine it:
  const selectedEvalId = messagesByEvalId?.evalId;
  const selectedLlmSession = selectedEvalId ? sessions[selectedEvalId] : null;
  const isLlmResponseInProgress = Boolean(
    selectedEvalId &&
    (
      selectedLlmSession?.responseStreaming ||
      selectedLlmSession?.streamWait ||
      busyPromptByEvalId?.[selectedEvalId]
    )
  );
  const processedImages = totalImages?.[selectedEvalId]?.[1] || 0;
  const canPauseWithData = canPause && processedImages >= 1;

  const smoothTime = useSmoothTimer({
    startTime: selectedRow.created_time,  // for Evaluating
    endTime: selectedRow.wait_time,       // for Queued
    status: selectedRow.status
  });


  const headerSmoothTime = useSmoothTimer({
    startTime: selectedRow.created_time,
    endTime: selectedRow.wait_time,
    status: selectedRow.status,
    queuedMode: "countup"
  });


  useEffect(() => {
    if (evaluationSelect?.[0]?.value) {
      const list = historyListRef.current;
      const selectedCard = historyRefs.current[evaluationSelect[0].value];

      if (list && selectedCard) {
        const listRect = list.getBoundingClientRect();
        const cardRect = selectedCard.getBoundingClientRect();
        const cardTop = cardRect.top - listRect.top + list.scrollTop;

        list.scrollTo({
          top: Math.max(cardTop - (list.clientHeight - cardRect.height) / 2, 0),
          behavior: "smooth",
        });
      }
    }
  }, [evaluationSelect]);


  const handleActions = async (actionType, evalId = evaluationSelect[0]?.value) => {
    try {
      if (actionType === "Terminate") {
        setCallStartTerminate(true);
        setIsTerminated(true);
      }

      const payload = {
        _id: evalId,
        action: actionType.toLowerCase(),
      };

      const token = getCokkie("jwtToken");
      const url = `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (actionType.toLowerCase() === "download" && result) {
        const downloadLink = document.createElement("a");
        downloadLink.href = result;
        downloadLink.download = "";
        downloadLink.target = "_blank";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } catch (error) {
      console.error("Error performing action:", error);
    } finally {
      setCallStartTerminate(false);
    }
  };

  const handleStopLlmEvaluation = async () => {
    const targetEvalId = messagesByEvalId?.evalId || evaluationSelect[0]?.value;
    if (!targetEvalId || callStartTerminate || isLlmResponseInProgress) return;

    setCallStartTerminate(true);
    try {
      await getDataFromApi(
        `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
        "POST",
        { _id: targetEvalId, action: "stop" }
      );
      closeLlmEvaluationStream(targetEvalId);
      setBusyPromptByEvalId((previous) => ({
        ...previous,
        [targetEvalId]: false,
      }));
      updateLLMSession((current) => {
        const history = Array.isArray(current.history) ? [...current.history] : [];
        const lastIndex = history.length - 1;
        if (lastIndex >= 0 && history[lastIndex]?.response === "Analysing...") {
          history[lastIndex] = {
            ...history[lastIndex],
            response: "No Response",
          };
        }

        return {
          ...current,
          history,
          contextHistories: {
            ...(current.contextHistories || {}),
            [current.activeContext || 1]: history,
          },
          loading: false,
          pendingPrompt: "",
          responseStreaming: false,
          streamWait: false,
        };
      }, targetEvalId);
    } catch (error) {
      console.error("Unable to stop LLM evaluation", error);
    } finally {
      setCallStartTerminate(false);
    }
  };
  const handlePauseResume = () => {
    // decide based on current status
    const paused = (messagesByEvalId?.status || selectedRow.status) === "Paused";

    if (paused) {
      if (!selectedRow.enable_resume) return;  // 🚫 block
      handleActions("Resume");
      setCallStart(true);
      setIsPaused(false);
    } else {
      // if you want the processedImages rule, also check it here
      const selectedEvalId = messagesByEvalId?.evalId;
      const processedImages = totalImages?.[selectedEvalId]?.[1] || 0;

      if (!selectedRow.enable_pause) return;   // 🚫 block
      if (processedImages < 1) return;         // 🚫 optional block

      handleActions("Pause");
      setCallStart(true);
      setIsPaused(true);
    }
  };

  const parseDevice = (deviceString) => {
    if (!deviceString) return { device: "", chipset: "" };

    const match = deviceString.match(/^(.*)\((.*)\)$/);

    return match
      ? { device: match[1].trim(), chipset: match[2].trim() }
      : { device: deviceString.trim(), chipset: "" };
  };

  const mappedTaskType =
    Tas_type_map[data?.info?.task_type] || data?.info?.task_type || "--";
  const uiModelName =
    model_Name || data?.info?.standard_model_name || data?.info?.name || displayModelName || "--";
  const uiTaskType =
    data?.info?.standard_task_type || mappedTaskType;

  const hubModelSlug = encodeURIComponent(
    data?.info?.name || finalModelNameRaw || displayModelName || finalModelName || ""
  );
  const hubModelInfoPath = hubModelSlug ? `/hub/${hubModelSlug}` : "/hub";
  const projectArchitecture = routeArchitecture || searchParams.get("architecture") || location?.state?.architecture;

  const handleProjectModelBreadcrumbClick = useCallback(() => {
    const projectModelName = data?.info?.name || selectedCard?.name || finalModelName;
    if (!projectId || !projectArchitecture || !projectModelName) return;

    navigate(
      `/projects/${projectId}/deepdeploy/${encodeURIComponent(projectArchitecture)}/model-info/${encodeURIComponent(projectModelName)}?architecture=${encodeURIComponent(projectArchitecture)}`,
      {
        state: {
          selectedCard: data?.info || selectedCard,
          through_project: true,
          version: data?.info?.version ?? modelVersion,
          projectId,
          architecture: projectArchitecture,
        },
      }
    );
  }, [
    data?.info,
    finalModelName,
    modelVersion,
    navigate,
    projectArchitecture,
    projectId,
    selectedCard,
  ]);

  const evaluationBreadcrumbContextLabel = "Evaluation";

  useEffect(() => {
    if (!fromProjects || !onPathDetailChange) return undefined;

    const modelLabel = uiModelName || displayModelName || "Model";

    onPathDetailChange({
      extras: [
        {
          label: modelLabel,
          onClick: handleProjectModelBreadcrumbClick,
        },
        { label: evaluationBreadcrumbContextLabel },
      ],
      onBack: handleEvaluationBreadcrumbBack,
    });

    return () => onPathDetailChange(null);
  }, [
    displayModelName,
    evaluationBreadcrumbContextLabel,
    fromProjects,
    handleEvaluationBreadcrumbBack,
    handleProjectModelBreadcrumbClick,
    onPathDetailChange,
    uiModelName,
  ]);

  const renderEvaluationBreadcrumb = (className = "", modelLabelOverride = null) => {
    if (!showEvaluationBreadcrumb) return null;

    const modelLabel = modelLabelOverride || uiModelName || "Model";
    const hasSelectedEvaluation = evaluationSelect.length > 0;
    const items = [];

    if (fromProjects && projectId) {
      items.push(
        { label: "Projects", onClick: () => navigate("/projects") },
        { label: "Deploy", onClick: () => navigate(`/projects/${projectId}/deepdeploy`) }
      );
      items.push({
        label: modelLabel,
        onClick:
          hasSelectedEvaluation || showFreshAnalytics
            ? () => {
                setShowFreshAnalytics(false);
                if (hasSelectedEvaluation) resetEvaluationWorkspace();
              }
            : undefined,
      });
      items.push({ label: evaluationBreadcrumbContextLabel });
    } else {
      items.push({ label: "Models", onClick: () => navigate("/hub") });
      items.push({
        label: modelLabel,
        onClick:
          hasSelectedEvaluation || showFreshAnalytics
            ? () => {
                setShowFreshAnalytics(false);
                if (hasSelectedEvaluation) resetEvaluationWorkspace();
              }
            : hubModelSlug
              ? () => navigate(hubModelInfoPath)
              : undefined,
      });
      items.push({ label: evaluationBreadcrumbContextLabel });
    }

    return (
      <PathBreadcrumb
        className={`evaluation-page-breadcrumb ${className}`.trim()}
        items={items}
        onBack={handleEvaluationBreadcrumbBack}
        aria-label="Evaluation path"
      />
    );
  };

  const renderLlmMetricCards = (selectedRow) => {
    const useLlmMetrics =
      isLLMTaskType &&
      data?.info?.task_type !== "ZeroShot Classification" &&
      data?.info?.task_type !== "ZeroShot Detection";

    return InnerMetrics.map((metric) => (
      <div
        key={metric.key}
        className={`evaluation-results-metric-stat evaluation-results-metric-stat--${metric.key}`}
      >
        <div className="evaluation-results-metric-stat__icon">
          {typeof metric.icon === "function"
            ? metric.icon(useLlmMetrics)
            : metric.icon}
        </div>
        <div className="evaluation-results-metric-stat__copy">
          <span className="evaluation-results-metric-stat__label">
            {useLlmMetrics ? metric.titleLLM : metric.title1(selectedRow)}
          </span>
          <strong className="evaluation-results-metric-stat__value">
            {metric.value(selectedRow, useLlmMetrics)}
          </strong>
        </div>
      </div>
    ));
  };

  const renderLlmMetricsPanel = (selectedRow) => (
    <section className="evaluation-results-metrics-view">
      <div className="evaluation-results-metrics-view__intro">
        <h3>Evaluation metrics</h3>
        <p>Performance summary for this evaluation run.</p>
      </div>
      <div className="evaluation-results-metrics-view__grid">
        {renderLlmMetricCards(selectedRow)}
      </div>
    </section>
  );

  const renderLlmResultsTopActions = (selectedRow, { includeNavigation = true } = {}) => {
    if (!isLLMTaskType) return null;

    const isRunningStatus =
      messagesByEvalId?.status === "starting" ||
      messagesByEvalId?.status === "Converting" ||
      messagesByEvalId?.status === "Evaluating" ||
      messagesByEvalId?.status === "Paused" ||
      messagesByEvalId?.status === "Queued";

    return (
      <div className="evaluation-results-llm-toolbar__actions-inner">
        {isRunningStatus && (
          <span className="evaluation-results-timer text-nowrap">
            ⏱{" "}
            {(selectedRow?.status === "Evaluating" ||
              selectedRow?.status === "Paused" ||
              selectedRow?.status === "Starting" ||
              selectedRow?.status === "Converting" ||
              selectedRow?.status === "Queued" ||
              selectedRow?.status === "queued")
              ? TimeFormatter(headerSmoothTime)
              : TimeFormatter(selectedRow?.completed_time)}
          </span>
        )}
        {includeNavigation && <button
          type="button"
          className={`evaluation-fresh-analytics-trigger${showLlmMetricsPanel ? " evaluation-fresh-analytics-trigger--active" : ""}`}
          onClick={() => {
            setShowLogsPane(false);
            setShowLlmMetricsPanel(true);
          }}
          title="View evaluation metrics"
        >
          <AnalyticsIcon className="wf-icon-small" />
          <span>Metrics</span>
        </button>}
        {includeNavigation && messagesByEvalId?.status !== "Queued" && (
          <button
            type="button"
            className={`evaluation-fresh-analytics-trigger${showLogsPane ? " evaluation-fresh-analytics-trigger--active" : ""}`}
            onClick={() => {
              setShowLogsPane((prev) => {
                const next = !prev;
                if (next) setShowLlmMetricsPanel(false);
                return next;
              });
            }}
            title={showLogsPane ? "Back to chat" : "View evaluation logs"}
          >
            {!showLogsPane ? (
              <CodeIcon className="wf-icon-small" />
            ) : (
              <ChatIcon className="wf-icon-small" />
            )}
            <span>{showLogsPane ? "Chat" : "Logs"}</span>
          </button>
        )}
        {String(messagesByEvalId?.status || "").trim().toLowerCase() === "evaluating" && (
          <button
            type="button"
            className="evaluation-fresh-analytics-trigger evaluation-fresh-analytics-trigger--danger"
            disabled={callStartTerminate || isLlmResponseInProgress}
            aria-busy={callStartTerminate}
            onClick={handleStopLlmEvaluation}
            title={
              isLlmResponseInProgress
                ? "Wait for the current response to finish before stopping the evaluation"
                : "Stop evaluation"
            }
          >
            <FontAwesomeIcon
              icon={callStartTerminate ? faSpinner : faStop}
              spin={callStartTerminate}
              style={{ fontSize: "12px" }}
            />
            <span>Stop</span>
          </button>
        )}
      </div>
    );
  };

  const handleDeleteEvaluation = (item, event) => {
    event?.stopPropagation?.();
    deletingEvalIdRef.current = item.value;
    setDeletingEvalId(item.value);
    setDeleteUrl(
      `${window.ENV.API_URL}/evaluations?evaluation_id=${item.value}&confirm=`
    );
    setChatBox(false);
    setMessagesByEvalId({ evalId: null });
    setEvaluationSelect([]);
    setLoadButton(false);
  };

  const handleSelectEvaluationHistory = (item) => {
    setEvaluationSelect([item]);
    setChatBox(true);
    setCollapse(false);
    setShowLeftPanel(false);
    setShowLogsPane(false);
    setShowFreshAnalytics(false);
  };

  const renderEvaluationHistoryItem = (item, row = null, { isSkeleton = false } = {}) => {
    const isActive = evaluationSelect?.[0]?.value === item.value;
    const label = String(item.label || "").replace(/_/g, " ");
    const isBest = bestEvalNames.has(item.label);
    const status = row?.status || "";
    const normalizedStatus = status.toLowerCase();
    const statusIcon = getEvaluationHistoryStatusIcon(status);
    const showStatusText = status && !statusIcon;
    const canDelete = normalizedStatus !== "evaluating";

    if (isSkeleton) {
      return (
        <div key={item.value} className="evaluation-history-item evaluation-history-item--skeleton">
          <div className="placeholder bg-secondary bg-opacity-25 rounded evaluation-history-item__title" />
          <div className="placeholder bg-secondary bg-opacity-25 rounded-pill evaluation-history-item__status-skeleton" />
        </div>
      );
    }

    return (
      <div
        key={item.value}
        ref={(el) => {
          historyRefs.current[item.value] = el;
        }}
        className={`evaluation-history-item${isActive ? " evaluation-history-item--active" : ""}`}
        onClick={() => handleSelectEvaluationHistory(item)}
        title={label}
      >
        <div className="evaluation-history-item__main">
          <span
            className="evaluation-history-item__best"
            aria-label={isBest ? "Best evaluation" : undefined}
            aria-hidden={!isBest}
          >
            {isBest && <AccuracyIcon className="wf-icon-small" />}
          </span>
          <span className="evaluation-history-item__title">{label}</span>
          {statusIcon && (
            <span
              className="evaluation-history-item__status-icon"
              title={statusIcon.label}
              aria-label={statusIcon.label}
            >
              <FontAwesomeIcon
                icon={statusIcon.icon}
                color={statusIcon.color}
                spin={Boolean(statusIcon.spin)}
              />
            </span>
          )}
          {showStatusText && (
            <span className={`evaluation-history-item__status evaluation-second-status-${normalizedStatus}`}>
              {status}
            </span>
          )}
        </div>

        {canDelete && (
          <button
            type="button"
            className="evaluation-history-item__delete plain-button2"
            aria-label={`Delete ${label}`}
            title="Delete evaluation"
            onClick={(event) => handleDeleteEvaluation(item, event)}
            disabled={deletingEvalId === item.value}
          >
            {deletingEvalId === item.value ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <DeleteIcon className="wf-icon-small" />
            )}
          </button>
        )}
      </div>
    );
  };

  const renderCompactCanvasToolbar = () => {
    if (!isCompactLayout) return null;

    return (
      <div className="evaluation-fresh-compact-toolbar">
        <button
          type="button"
          className="evaluation-fresh-history-trigger"
          onClick={(event) => {
            event.stopPropagation();
            setShowLeftPanel(true);
          }}
        >
          <HistoryIcon className="wf-icon-small" />
          <span>History</span>
        </button>
      </div>
    );
  };

  const loadingBreadcrumbModelLabel =
    displayModelName || finalModelName || "Model";

  const isHubModelPage =
    !fromProjects &&
    !window.location.pathname.endsWith("/evaluation") &&
    window.location.pathname !== "/hub/model_page";

  const formatHubText = (value, fallback = "--") => {
    if (Array.isArray(value)) return formatHubText(value[0], fallback);
    if (value === undefined || value === null || value === "") return fallback;
    const normalized = value.toString().replace(/_/g, " ");
    if (normalized.toLowerCase() === "cavalry binary") return "cavalry_binary";
    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getHubMetric = (modelInfo) => {
    const benchmarkInfo = modelInfo?.config?.Benchmark_Info;
    const firstMetric =
      modelInfo?.metrics?.[0] ||
      benchmarkInfo?.Benchmarkings?.[0] ||
      {};
    return {
      name: benchmarkInfo?.["Metric Name"] || "mAP",
      value:
        firstMetric?.["Metric Value(%)"] ??
        firstMetric?.metric_value ??
        firstMetric?.value ??
        "--",
      latency:
        firstMetric?.["Performance(ms)"] ??
        firstMetric?.performance ??
        firstMetric?.latency ??
        "--",
      deviceName: firstMetric?.["device name"] || "--",
      deviceType: firstMetric?.["device type"] || "--",
    };
  };

  const getHubGraphFormat = (modelInfo) =>
    modelInfo?.model_graph_format?.[0] ||
    modelInfo?.config?.Model_Info?.["Model Input Format"] ||
    "--";

  const getHubThumbnail = (modelInfo) => {
    const thumbnailUrl = modelInfo?.config?.data_config?.thumbnail?.url;
    if (thumbnailUrl?.startsWith("https")) return thumbnailUrl;
    if (thumbnailUrl) return `data:image/jpeg;base64,${thumbnailUrl}`;

    const fallbackByName = {
      RTMDet: "/ico/EfficientNetV2.png",
      Resnet50: "/ico/Resnet50latest.png",
      topformer: "/ico/topformerlatest.png",
      longclip: "/ico/longcliplatest.png",
      convnext: "/ico/Convnext.png",
      rtmpose: "/ico/keypoint.png",
      yolox: "/ico/yolox.png",
      yolov5: "/ico/yolox.png",
      retinaface: "/ico/retinafacelatest.png",
      facenet: "/ico/Facenetlatest.png",
      "InternVL-3.5-1B": "/ico/InternVL.png",
    };

    return fallbackByName[modelInfo?.name] || "/ico/objectdetect.png";
  };

  const getHubObjects = (modelInfo) =>
    (modelInfo?.config?.Model_Info?.["Objects of Interest"] || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  const getHubDeviceCount = (statusMap = {}) =>
    Object.values(statusMap).filter((status) => status?.toLowerCase?.() === "active").length ||
    Object.keys(statusMap).length;

  const renderHubEvalDrawer = () => (
    <div className={`eval-right-drawer-overlay ${!openEvalDrawer && 'd-none'}`} onClick={() => { setOpenEvalDrawer(false); setLoadButton(false) }}>
      <div
        className="eval-right-drawer-panel p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <CvEvaluationField
          key={`hub-evaluation-${initialEvaluationSource || "default"}-${openEvalDrawer ? "open" : "closed"}`}
          ref={rendertextfieldref}
          data={data}
          initialDataSource={initialEvaluationSource}
          initialSuggestedImages={selectedCvSampleImages}
          hideTestDataSelection
          onEvaluationCreated={(result, selectedhw, imageslength) => {
            setPendingCreatedEvaluationId(result?.evaluation_id || null);
            setSelectedCvSampleImages([]);
            setInitialEvaluationSource("");
            setOpenEvalDrawer(false);
            setCallStart(true);
            setCollapse(false);
            settargethw(selectedhw);
            setimageslength(imageslength);
          }}
          table_loading={load}
          evalStatus={messagesByEvalId}
          isTeriminated={isTeriminated}
          chipset={chipset}
          compression={compression}
          setOpenEvalDrawer={setOpenEvalDrawer}
          setLoadButton={setLoadButton}
          loadButton={loadButton}
          deviceStatusMap={Object?.entries(latestDeviceStatus).length > 0 ? latestDeviceStatus : data?.device_status}
          memRemainingMB={memRemainingMB}
        />
      </div>
    </div>
  );



  if (loading && !silentRefetching) {
    return (
      <>

        <div className={`evaluation-container2 evaluation-container2--fresh${fromProjects ? " evaluation-container2--project" : " de-width"}`} style={{ display: 'flex', flexDirection: 'column', height: evaluationLayoutHeight, minHeight: evaluationLayoutHeight, maxHeight: evaluationLayoutHeight, overflow: 'hidden' }}>
          {renderEvaluationBreadcrumb("evaluation-page-breadcrumb", loadingBreadcrumbModelLabel)}
          <div className="evaluation-container2__body d-flex gap-4 flex-fill min-h-0">

            {/* LEFT PANEL */}
            {!isCompactLayout &&
              <div
                className="left-pannel container-card evaluation-second-container bg-network p-0 h-100 d-flex flex-column"
                style={{ minWidth: "23rem", flex: "0 0 23rem", minHeight: 0 }}
              >
                <div className="evaluation-sidebar-skeleton__menu" aria-hidden="true">
                  <div className="evaluation-sidebar-skeleton__title evaluation-sidebar-skeleton__block" />
                  <div className="evaluation-sidebar-skeleton__nav">
                    {["new", "details", "summary", "compatibility"].map((item, index) => (
                      <div
                        key={item}
                        className={`evaluation-sidebar-skeleton__nav-row${index === 0 ? " evaluation-sidebar-skeleton__nav-row--active" : ""}`}
                      >
                        <span className="evaluation-sidebar-skeleton__nav-icon evaluation-sidebar-skeleton__block" />
                        <span className="evaluation-sidebar-skeleton__nav-label evaluation-sidebar-skeleton__block" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Evaluation history */}
                <div className="flex-fill d-flex flex-column min-h-0 evaluation-sidebar-skeleton__history">
                  <div className="d-a justify-content-between evaluation-second-section-title d-flex evaluation-sidebar-skeleton__history-header">
                    <span className="d-a gap-1"><svg className="w-6 h-6 eval-history-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                          d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
                      </svg> EVALUATION HISTORY</span>
                    <div className="evaluation-sidebar-skeleton__history-tools" aria-hidden="true">
                      <span className="evaluation-sidebar-skeleton__search evaluation-sidebar-skeleton__block" />
                      <span className="evaluation-sidebar-skeleton__count evaluation-sidebar-skeleton__block" />
                    </div>
                  </div>
                  <div className="overflow-hidden flex-fill min-h-0 evaluation-sidebar-skeleton__history-list">
                    {[...Array(11)].map((_, idx) => (
                      <div
                        key={idx}
                        className={`evaluation-sidebar-skeleton__history-row${idx === 5 ? " evaluation-sidebar-skeleton__history-row--active" : ""}`}
                      >
                        <span className="evaluation-sidebar-skeleton__history-label evaluation-sidebar-skeleton__block" />
                        {idx === 5 && <span className="evaluation-sidebar-skeleton__menu-dot evaluation-sidebar-skeleton__block" />}
                      </div>
                    ))}
                  </div>
                </div>
              </div>}

            {/* RIGHT PANEL */}
            <div className="flex-fill eval-prompt-area d-flex flex-column min-h-0" style={{ minWidth: 0 }}>
              <div className="evaluation-fresh-page w-100 h-100 px-0 pb-0 d-flex align-items-center justify-content-center">
                <div
                  className="d-flex flex-column align-items-center justify-content-center w-100"
                  style={{ minHeight: "65vh" }}
                >
                  <Loader />
                  <h5 className="fw-semibold mb-2 text-center mt-3">
                    Loading evaluation...
                  </h5>
                  <p className="text-secondary text-center mb-0 px-3">
                    Please wait while we prepare the model workspace.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>

    )
  }

  // ✅ ERROR UI — MUST BE FIRST
  if (!loading && hasError && !noSearchResults) {
    const status =
      error?.status ||
      error?.response?.status ||
      error?.response?.data?.status ||
      500;

    return (
      <>
        <div
          className={`d-j-a h-100 flex-column align-items-center pt-5${fromProjects ? " evaluation-container2--project" : " de-width"}`}
          style={{ minHeight: "100%" }}
        >
          <div
            style={{
              backgroundColor: "#fff8d5",
              border: "1px solid #ffe7a3",
              borderRadius: "4px",
              padding: "14px 24px",
              maxWidth: "600px",
              textAlign: "center",
              fontSize: "0.95rem",
              color: 'black'
            }}
          >
            {status === 404
              ? "Model not found (404)."
              : status === 403
                ? "You don’t have permission to view this model."
                : status === 500 || status === 502
                  ? "Server error. Please try again."
                  : "Unable to load model details right now."}
          </div>

          <div className="mt-3 btn-click">
            <Button color="primary" onClick={refetch}>
              Retry
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (isHubModelPage && data?.info) {
    const info = data.info;
    const benchmark = getHubMetric(info);
    const graphFormat = getHubGraphFormat(info);
    const objects = getHubObjects(info);
    const activeTargets = getHubDeviceCount(data?.device_status || {});
    const evalTotal = Number(data?.user_limits?.total?.total_evaluations || 0);
    const evalUsed = Number(data?.user_stats?.total_evaluations || 0);
    const memoryTotal = Number(data?.user_limits?.total?.memory_usage || 0);
    const memoryUsed = Number(data?.user_stats?.memory_consumed || 0);
    const evalPercent = evalTotal ? Math.min(100, (evalUsed / evalTotal) * 100) : 0;
    const memoryPercent = memoryTotal ? Math.min(100, (memoryUsed / memoryTotal) * 100) : 0;
    const chartRows = [
      { label: "Raspberry Pi 3", value: 38 },
      { label: "NXP i.MX 8M Plus", value: 86 },
      { label: "CV25", value: 138 },
      { label: "CV72", value: Number(benchmark.latency) ? Math.round(1000 / Number(benchmark.latency)) : 333 },
      { label: "N1-655", value: 555 },
      { label: "N1", value: 476 },
      { label: "NVIDIA Jetson Orin Nano", value: 833 },
    ];
    const maxChartValue = Math.max(...chartRows.map((row) => row.value), 1);
    const objectPreview = objects.slice(0, 80);

    return (
      <>
        <div className="hub-model-detail de-width">
          <button
            className="hub-model-detail__back plain-button2"
            onClick={() => navigate(-1)}
          >
            <BackIcon className="wf-icon" /> Back to Model Hub
          </button>

          <section className="hub-model-hero">
            <div className="hub-model-hero__copy">
              <div className="hub-model-hero__title-row">
                <h1>{uiModelName}</h1>
                {info.model_variant && <span>{info.model_variant}</span>}
                {uiTaskType && <span className="hub-model-hero__task">{uiTaskType}</span>}
              </div>
              <h2>
                Real-time Edge {formatHubText(uiTaskType, "AI")} optimized for {formatHubText(info.organization, "edge")} CVflow.
              </h2>
              <p>
                {info.config?.Benchmark_Info?.["Model Description"] ||
                  info.model_description ||
                  `${uiModelName} is available in the Model Hub for benchmarking, evaluation, and deployment to supported edge targets. This overview summarizes model metadata, benchmark readiness, graph format, and target compatibility.`}
              </p>

              <div className="hub-model-spec-grid">
                <div>
                  <span>Framework</span>
                  <strong>{formatHubText(info.framework)}</strong>
                </div>
                <div>
                  <span>Graph Format</span>
                  <strong>{graphFormat}</strong>
                </div>
                <div>
                  <span>Benchmark</span>
                  <strong>{formatHubText(info.benchmark_dataset)}</strong>
                </div>
                <div>
                  <span>Access Mode</span>
                  <strong><i /> {formatHubText(info.access)}</strong>
                </div>
              </div>

              <div className="hub-model-actions">
                <Button color="primary" className="btn-click" onClick={handleStartEvaluation}>
                  <Evaluation className="wf-icon text-white" /> Evaluate Model
                </Button>
                <Button outline color="primary" className="btn-click" onClick={() => setOpenModal(true)}>
                  <ChipsetIcon className="wf-icon" /> Deploy to Projects
                </Button>
              </div>
            </div>

            <div className="hub-model-hero__visual">
              <img
                src={getHubThumbnail(info)}
                alt={uiModelName}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = "/ico/objectdetect.png";
                }}
              />
              <div className="hub-model-detection-box hub-model-detection-box--one">
                <span>{formatHubText(uiTaskType)} {benchmark.value}%</span>
              </div>
              <div className="hub-model-detection-box hub-model-detection-box--two">
                <span>{formatHubText(benchmark.deviceType)} active</span>
              </div>
              <div className="hub-model-visual-status">
                <strong><i /> CVFLOW ACTIVE INFERENCE</strong>
                <span>Stream Source: {benchmark.deviceName}</span>
              </div>
            </div>
          </section>

          <section className="hub-model-usage-card">
            <div className="hub-model-org">
              <ChipsetIcon className="wf-icon-blue" />
              <div>
                <strong>{formatHubText(info.organization, "Organization")}</strong>
                <span>ID: {info.created_by}</span>
              </div>
            </div>
            <div className="hub-model-quota">
              <div className="hub-model-quota__head">
                <span>Total Evaluations</span>
                <strong>{evalUsed} / {evalTotal || "--"}</strong>
              </div>
              <div className="hub-model-progress"><span style={{ width: `${evalPercent}%` }} /></div>
              <small>Monthly processing cycle limits</small>
            </div>
            <div className="hub-model-quota">
              <div className="hub-model-quota__head">
                <span>Edge Memory Allocation</span>
                <strong>{memoryUsed.toFixed(3)} / {memoryTotal || "--"} GB</strong>
              </div>
              <div className="hub-model-progress hub-model-progress--green"><span style={{ width: `${memoryPercent}%` }} /></div>
              <small>CVflow hardware partition weight</small>
            </div>
            <div className="hub-model-system">
              <span>System Activity</span>
              <strong>{activeTargets} targets active</strong>
            </div>
          </section>

          <section className="hub-model-panel">
            <div className="hub-model-panel__header">
              <div>
                <h3>Edge Hardware Benchmarking Profiler</h3>
                <p>Verify execution speeds, memory maps, and accuracy states across physical edge targets.</p>
              </div>
              <div className="hub-model-segment">
                <span>float32 (Unquantized)</span>
                <strong>Quantized (Cavalry)</strong>
              </div>
            </div>
            <div className="hub-model-profiler">
              <aside>
                <label>Compilation Target</label>
                <strong>Model Graph Format</strong>
                <div className="hub-model-select">{formatHubText(info.organization)} {graphFormat}</div>
                <strong>Select Target Device</strong>
                <div className="hub-model-select">{formatHubText(benchmark.deviceType)} ({benchmark.deviceName})</div>
                <span className="hub-model-live"><i /> DEVICE TELEMETRY LIVE</span>
              </aside>
              <div className="hub-model-metric-card">
                <span>Inference Time</span>
                <strong>{benchmark.latency}<small> ms</small></strong>
                <em>Latency per batch</em>
              </div>
              <div className="hub-model-metric-card">
                <span>Throughput</span>
                <strong className="text-primary">{Number(benchmark.latency) ? Math.round(1000 / Number(benchmark.latency)) : "--"}<small> FPS</small></strong>
                <em>Frames per second</em>
              </div>
              <div className="hub-model-metric-card">
                <span>Memory Usage</span>
                <strong>{Math.round(memoryUsed * 150) || 124}<small> MB</small></strong>
                <em>SRAM footprint</em>
              </div>
              <div className="hub-model-metric-card">
                <span>{formatHubText(benchmark.name)} Accuracy</span>
                <strong className="hub-model-green">{benchmark.value}<small> %</small></strong>
                <em>{formatHubText(info.benchmark_dataset)} Dataset</em>
              </div>
            </div>
            <div className="hub-model-panel__note">
              Hardware acceleration: <strong>{formatHubText(info.organization)} CVflow Engine</strong> is used when compatible graph binaries are available.
              <span>Verified on {benchmark.deviceName}</span>
            </div>
          </section>

          <div className="hub-model-section-title">
            <span>Performance & Diagnostics Workspace</span>
            <h2>Edge Hardware Benchmarking & Architecture Matrix</h2>
          </div>

          <section className="hub-model-insights">
            <div><span>Optimal Frame Host</span><strong>NVIDIA Orin Nano</strong><p>Throughput peaks @ 833 FPS</p></div>
            <div><span>Lowest Edge Power-Cost</span><strong className="hub-model-green">Ambarella N1-655</strong><p>46.1% mAP at only 1.8ms</p></div>
            <div><span>Precision Divergence</span><strong>&lt; 1.5% Loss</strong><p>Between FP32 and INT8 Cavalry</p></div>
          </section>

          <section className="hub-model-panel">
            <div className="hub-model-panel__header">
              <div>
                <h3>Performance Comparison Matrices</h3>
                <p>Metric graphs charting {uiModelName} processing metrics over target processors.</p>
              </div>
              <div className="hub-model-segment"><strong>Throughput (FPS)</strong><span>Latency (ms)</span><span>mAP (%)</span></div>
            </div>
            <div className="hub-model-chart">
              {chartRows.map((row) => (
                <div key={row.label} className="hub-model-chart__bar">
                  <span style={{ height: `${Math.max(8, (row.value / maxChartValue) * 100)}%` }} />
                  <strong>{row.label}</strong>
                </div>
              ))}
            </div>
            <div className="hub-model-panel__note">
              Optimal latency performance achieved via dedicated model graph pruning.
              <span>Data compiled from {evalUsed || data?.eval_ids_names?.length || 0} active test cycles.</span>
            </div>
          </section>

          <section className="hub-model-rankings">
            <div className="hub-model-panel">
              <h3>Accuracy Rank by Platform</h3>
              {["Ambarella N1-655", "NVIDIA Jetson Orin Nano", "Ambarella CV72", "Ambarella N1", "Ambarella CV25", "NXP i.MX 8M Plus", "Raspberry Pi 3"].map((name, index) => (
                <div className="hub-model-rank-row" key={name}>
                  <span>#{index + 1} {name}</span>
                  <strong>{(46.1 - index * 1.4).toFixed(1)}% mAP</strong>
                </div>
              ))}
            </div>
            <div className="hub-model-panel">
              <h3>SRAM Memory Footprint</h3>
              {["Raspberry Pi 3", "NXP i.MX 8M Plus", "Ambarella CV25", "Ambarella CV72", "Ambarella N1-655", "Ambarella N1", "NVIDIA Jetson Orin Nano"].map((name, index) => (
                <div className="hub-model-rank-row" key={name}>
                  <span>#{index + 1} {name}</span>
                  <strong>{[45, 78, 89, 124, 195, 210, 256][index]} MB</strong>
                </div>
              ))}
            </div>
          </section>

          <section className="hub-model-panel">
            <div className="hub-model-panel__header">
              <div>
                <h3>Chipset Architecture Compatibility Map</h3>
                <p>Verify compilation boundaries, hardware accelerator bindings, and target status.</p>
              </div>
              <span className="hub-model-compiler">Cavalry Compiler v2.0.4-L</span>
            </div>
            <div className="hub-model-compat">
              <div>
                <h4><i /> Supported & Hardware Accelerated</h4>
                {["Ambarella CV72 Series", "Ambarella N1-655 / N1 Series", "Ambarella CV25 Series"].map((name, index) => (
                  <article className="hub-model-compat-card hub-model-compat-card--good" key={name}>
                    <strong>{name}</strong>
                    <span>{index === 2 ? "LEGACY ACTIVE" : "FULLY OPTIMIZED"}</span>
                    <p>Full hardware native binary mapping with optimized throughput.</p>
                    <small>Engine: CVflow Neural Engine · Precision: INT8 / Quantized Cavalry · Throughput: {index === 0 ? 333 : index === 1 ? 555 : 138} FPS</small>
                  </article>
                ))}
              </div>
              <div>
                <h4 className="hub-model-compat__bad"><i /> Unsupported Architecture & CPU Fallbacks</h4>
                {["NVIDIA Jetson Orin Nano / Nano 2", "NXP i.MX 8M Plus", "Raspberry Pi 3 / Pi 4"].map((name) => (
                  <article className="hub-model-compat-card hub-model-compat-card--bad" key={name}>
                    <strong>{name}</strong>
                    <span>UNSUPPORTED</span>
                    <p>Cannot execute compiled Cavalry binary package model files.</p>
                    <small>Precision: FP16 / FP32 ONNX only</small>
                  </article>
                ))}
              </div>
            </div>
            <div className="hub-model-warning">
              Compilation Ineligibility: Non-Ambarella system targets cannot execute Cavalry binary formats.
            </div>
          </section>

          <section className="hub-model-panel">
            <div className="hub-model-panel__header">
              <div>
                <h3>Technical Configuration & Metadata</h3>
                <p>Underlying model JSON coordinates, S3 bucket endpoints, and preprocessing parameters.</p>
              </div>
            </div>
            <div className="hub-model-metadata">
              <div>
                <h4>Registry Identification</h4>
                <dl>
                  <dt>Model ID</dt><dd>{info._id}</dd>
                  <dt>Registry Creator</dt><dd>{info.created_by}</dd>
                  <dt>Ownership</dt><dd>{formatHubText(info.ownership)}</dd>
                  <dt>Organization Target</dt><dd>{formatHubText(info.organization)}</dd>
                  <dt>Platform Upload Location</dt><dd>{info.model_location}</dd>
                </dl>
              </div>
              <div>
                <h4>Preprocessing Directives</h4>
                <dl>
                  <dt>Grayscale Input</dt><dd>{info.config?.Model_Info?.Preprocessing?.GrayScale ? "Enabled" : "Disabled (Full RGB)"}</dd>
                  <dt>Max Input Resolution</dt><dd>{info.config?.Model_Info?.Preprocessing?.["max input resolution"] || "--"} px</dd>
                  <dt>Interpolation Mode</dt><dd>{info.config?.Model_Info?.Preprocessing?.interpolation || "--"}</dd>
                  <dt>Normalization Formula</dt><dd>{info.config?.Model_Info?.Preprocessing?.normalization_method || "--"}</dd>
                </dl>
              </div>
            </div>

            {objectPreview.length > 0 && (
              <div className="hub-model-objects">
                <div className="hub-model-objects__head">
                  <div>
                    <h4>Objects of Interest ({objects.length} decodable labels)</h4>
                    <p>Calculable labels mapped natively inside the {uiModelName} decoder dictionary.</p>
                  </div>
                  <div className="hub-model-object-search">
                    <FontAwesomeIcon icon={faSearch} /> Filter dictionary vocabulary...
                  </div>
                </div>
                <div className="hub-model-object-list">
                  {objectPreview.map((item) => <span key={item}>{item}</span>)}
                </div>
              </div>
            )}
          </section>
        </div>

        <Modal
          className=""
          isOpen={openModal}
          onRequestClose={closeModal}
          contentLabel="Deploy Model"
        >
          <Button
            className="float-right"
            color="transparant"
            onClick={closeModal}
            style={{
              position: "sticky",
              zIndex: 9999,
              top: 0,
              marginRight: "-8px",
            }}
          >
            <FontAwesomeIcon icon={faTimes} color="grey" size="lg" />
          </Button>
          <Deploy />
        </Modal>
        {renderHubEvalDrawer()}
      </>
    );
  }






  // ------------- Main Render JSX ------------- //
  return (
    <>
      <div
        className="mt-3"
        style={{ display: 'none' }}
      >
        <EvaluationTablestream
          _id={data?.info?._id}
          created_by={data?.info?.current_user_id}
          refetch={refetch}
          ref={childRef}
          onDataChange={handleDataChange}
          pageChange={pageChange}
          setLoadingState={setLoading}
          callStart={callStart}
          callStartTerminate={callStartTerminate}
          pageSize={evaluationHistoryPageSize}
          onStreamSettled={() => setIsLoadingMoreEvaluations(false)}
          isTeriminated={isTeriminated}
          setIsTerminated={setIsTerminated}
          evaluation={"Evaluation"}
          evaluationhistorylength={evaluationhistorylength}
          setEvaluationHistoryLength={setEvaluationHistoryLength}
        />
      </div>




      {data ? (
        <>
          <div
            className={`evaluation-container2 evaluation-container2--fresh${fromProjects ? " evaluation-container2--project" : " de-width"} ${isCompactLayout ? "pt-0 evaluation-container2--compact" : ""}${hasLlmResultsView ? " evaluation-container2--llm-results" : ""}`}
            onClick={(e) => { setShowLeftPanel(false) }}
            style={{
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              overflow: "hidden",
              height: evaluationLayoutHeight,
              minHeight: evaluationLayoutHeight,
              maxHeight: evaluationLayoutHeight,
            }}
          >

            {hasLlmResultsView ? (
              <div className="evaluation-llm-page-header">
                {renderEvaluationBreadcrumb("evaluation-page-breadcrumb")}
                <div className="evaluation-llm-page-header__actions">
                  {renderLlmResultsTopActions(selectedRow)}
                </div>
              </div>
            ) : (
              renderEvaluationBreadcrumb("evaluation-page-breadcrumb")
            )}

            <div
              className="evaluation-container2__body d-flex justify-content-between gap-4"
              style={{
                flex: "1 1 auto",
                minHeight: 0,
                overflow: "hidden",
                alignItems: "stretch",
              }}
            >

              <div className={`${isCompactLayout ? (showLeftPanel ? 'open-from-left' : 'd-none') : 'left-pannel'} container-card evaluation-second-container bg-network eval-prompt-area p-0 d-flex flex-column`} style={{ height: "100%", minHeight: 0, minWidth: '23rem', flex: "0 0 23rem", overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>

                {/* --- Model workspace actions --- */}
                <div className="evaluation-sidebar-menu">
                  <div className="evaluation-sidebar-menu__header">
                    <span className="evaluation-sidebar-menu__model" title={uiModelName}>{uiModelName}</span>
                    {isCompactLayout && (
                      <button className="mobile-leftpanel-btn plain-button2" onClick={() => setShowLeftPanel(false)}>
                        <CloseIcon className="wf-icon text-secondary" />
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    className={`evaluation-sidebar-menu__item evaluation-sidebar-menu__item--new${evaluationSelect.length === 0 && !showFreshAnalytics ? " is-active" : ""}`}
                    onClick={handleNewConversation}
                  >
                    <span className="evaluation-sidebar-menu__icon" aria-hidden="true">
                      <HistoryIcon className="wf-icon-small" />
                    </span>
                    <span className="evaluation-sidebar-menu__label">{isLLMTaskType ? "New chat" : "New evaluation"}</span>
                  </button>

                  <button
                    type="button"
                    className="evaluation-sidebar-menu__item"
                    onClick={() => setShowModelInfoModal(true)}
                  >
                    <span className="evaluation-sidebar-menu__icon" aria-hidden="true">
                      <ModelIcon className="wf-icon-small" />
                    </span>
                    <span className="evaluation-sidebar-menu__label">Model Meta Data</span>
                  </button>

                  <button
                    type="button"
                    className="evaluation-sidebar-menu__item"
                    onClick={() => {
                      setLayerModalType("summary");
                      setShowLayerModal(true);
                      setShowLeftPanel(false);
                      setSummaryfilter(null);
                    }}
                  >
                    <span className="evaluation-sidebar-menu__icon" aria-hidden="true">
                      <TableIcon className="wf-icon-small" />
                    </span>
                    <span className="evaluation-sidebar-menu__label">Model Summary</span>
                  </button>

                  <button
                    type="button"
                    className="evaluation-sidebar-menu__item"
                    onClick={() => {
                      setLayerModalType("compatibility");
                      setShowLayerModal(true);
                      setShowLeftPanel(false);
                      setCompatibilityfilterfamily(null);
                      setCompatibilityfilterchipset(null);
                    }}
                  >
                    <span className="evaluation-sidebar-menu__icon" aria-hidden="true">
                      <ChipsetIcon className="wf-icon-small" />
                    </span>
                    <span className="evaluation-sidebar-menu__label">Hardware Compatibility</span>
                  </button>

                  {!fromProjects && data?.info?._id && (
                    <button
                      type="button"
                      className={`evaluation-sidebar-menu__item${showFreshAnalytics ? " is-active" : ""}`}
                      onClick={() => {
                        resetEvaluationWorkspace();
                        setShowFreshAnalytics(true);
                        setShowLogsPane(false);
                        setShowLlmMetricsPanel(false);
                        setShowLeftPanel(false);
                      }}
                    >
                      <span className="evaluation-sidebar-menu__icon" aria-hidden="true">
                        <LeaderboardIcon className="wf-icon-small" />
                      </span>
                      <span className="evaluation-sidebar-menu__label">Leaderboard</span>
                    </button>
                  )}
                </div>
                {/* --- Evaluation History --- */}
                <div className="evaluation-second-history-container flex-fill d-flex flex-column min-h-0">

                  <div className="evaluation-second-section-title evaluation-history-header d-a justify-content-between mb-3 pt-2" style={{ height: '25px' }}>
                    {!showHistorySearch ?
                      <>
                        <span className="evaluation-history-heading">
                          <span className="evaluation-sidebar-menu__icon" aria-hidden="true">
                            <RoundedClockIcon className="eval-history-card-icon wf-icon-small" />
                          </span>
                          <span className="evaluation-sidebar-menu__label">EVALUATION HISTORY</span>
                        </span>
                      </>
                      :

                      <div className="d-a me-1  ps-3 rounded bg-transparent flex-fill m-0" style={{ border: '1px solid var(--card-color)', height: '23px' }}>
                        <FontAwesomeIcon
                          className="plain-button2"
                          icon={faSearch}
                          style={{
                            position: "relative",
                            opacity: "0.8",
                            color: 'gray'
                          }}
                        />
                        <input
                          autoFocus
                          type="text"
                          className="border-0 outline-none bg-transparent p-0 ps-3 w-100 text"
                          style={{ outline: 'none' }}
                          placeholder="Search evaluations (name, device, dataset, status)"
                          title="Search evaluations (name, device, dataset, status)"
                          value={historySearch}
                          onChange={(e) => setHistorySearch(e.target.value)}
                        />

                        <div className="vertical-divider ms-2" style={{ height: '10px' }} />

                        <button className="plain-button2 text-danger" onClick={() => { setShowHistorySearch(false); setHistorySearch('') }}>
                          <CloseIcon className="wf-icon text-secondary-small" />
                        </button>
                      </div>}

                    <div className="d-a gap-1">
                      {!showHistorySearch &&
                        <button
                          className="plain-button2"
                          onClick={() => setShowHistorySearch(true)}
                          title="Search evaluations"
                        >
                          <FontAwesomeIcon
                            className="plain-button2"
                            icon={faSearch}
                            style={{
                              position: "relative",
                              opacity: "0.8",
                              color: 'gray'
                            }}
                          />
                        </button>}

                      <span className="evaluation-second-count-badge">
                        {historySearch
                          ? filteredEvalHistory.length
                          : (childData?.total_evaluations ?? childData?.eval_ids_names?.length ?? 0)}
                      </span>

                    </div>

                  </div>

                  <div ref={historyListRef} className="evaluation-second-list flex-fill min-h-0">
                    {(childData?.eval_ids_names?.length > 0 &&
                      (loading || !childData))
                      ? (
                        <>
                          {data?.eval_ids_names?.map((item) =>
                            renderEvaluationHistoryItem(item, null, { isSkeleton: true })
                          )}
                        </>
                      ) : (
                        <>
                          {filteredEvalHistory.map((item, index) => {
                            const row =
                              childData.device_table.data.find((r) => r.eval_id === item.value) ||
                              childData.device_table.data[index];

                            return renderEvaluationHistoryItem(item, row);
                          })}
                          {!historySearch && hasMoreEvaluationHistory && (
                            <button
                              type="button"
                              className="evaluation-history-load-more"
                              onClick={handleLoadMoreEvaluationHistory}
                              disabled={isLoadingMoreEvaluations}
                              aria-busy={isLoadingMoreEvaluations}
                              aria-label="Load more evaluations"
                            >
                              {isLoadingMoreEvaluations && (
                                <FontAwesomeIcon
                                  className="evaluation-history-load-more__spinner"
                                  icon={faSpinner}
                                  spin
                                  aria-hidden="true"
                                />
                              )}
                              <span>{isLoadingMoreEvaluations ? "Loading more" : "Load more"}</span>
                            </button>
                          )}
                        </>
                      )}
                  </div>
                </div>


              </div>











              <div
                className={`flex-fill eval-prompt-area${isCompactLayout ? " eval-prompt-area--compact" : ""}${hasLlmResultsView ? " eval-prompt-area--llm-results" : ""}`}
                style={{
                  flex: isCompactLayout ? "1 1 auto" : undefined,
                  height: "100%",
                  minWidth: 0,
                  minHeight: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >

                {/* EMPTY STATE WHEN NO EVALUATION SELECTED */}
                {(!childData?.eval_ids_names || evaluationSelect.length === 0) && (
                  <div className="evaluation-fresh-page w-100 h-100 px-0 pb-0">
                    <div className={`evaluation-fresh-canvas${isCompactLayout ? " evaluation-fresh-canvas--compact" : " container-card"}`}>
                      {renderCompactCanvasToolbar()}

                      {showFreshAnalytics && !fromProjects && data?.info?._id ? (
                        <section className="evaluation-fresh-analytics-panel">
                          <div className="evaluation-fresh-analytics-panel__header">
                            <div className="evaluation-fresh-analytics-panel__title">
                              <LeaderboardIcon className="wf-icon-small" />
                              <span>Evaluation Leaderboard</span>
                            </div>
                          </div>
                          <div className="evaluation-fresh-analytics-panel__body">
                            <EvaluationAnalytics modelId={data.info._id} embedded />
                          </div>
                        </section>
                      ) : (
                    <section className="evaluation-fresh-start-section evaluation-fresh-start-section--ai">
                      <div className={`evaluation-empty-state evaluation-empty-state--fresh evaluation-empty-state--ai d-j-a`}>

                        <div className="evaluation-ai-hero">
                          {/* <p className="evaluation-ai-eyebrow">Model evaluation</p> */}
                          <h3 className={`evaluation-empty-title evaluation-ai-greeting text${!isLLMTaskType ? " evaluation-ai-greeting--benchmark" : ""}`}>
                            {!isLLMTaskType ? ("Benchmark model performance with custom data...") : ("What would you like to try?")}
                          </h3>
                        </div>
                        {isLLMTaskType ? (
                          limitReached ? (
                            // ✅ RED UPGRADE PANEL (if eval OR memory reached)
                            <div className="eval-limit-card">
                              <div className="eval-limit-icon">💳</div>

                              <div className="eval-limit-title">Evaluation Limit Reached</div>

                              <div className="eval-limit-subtitle">
                                {evalLimitReached && memLimitReached && tokenLimitReached && (
                                  <>You have used all {FREE_EVAL_LIMIT} free evaluations, all {FREE_MEM_LIMIT} free memory, and all {FREE_TOKEN_LIMIT} free tokens.</>
                                )}

                                {tokenLimitReached && !evalLimitReached && !memLimitReached && (
                                  <>You have used all {FREE_TOKEN_LIMIT} free tokens.</>
                                )}

                                {evalLimitReached && !memLimitReached && !tokenLimitReached && (
                                  <>You have used all {FREE_EVAL_LIMIT} free evaluations.</>
                                )}

                                {!evalLimitReached && memLimitReached && !tokenLimitReached && (
                                  <>You have used all {FREE_MEM_LIMIT} free memory.</>
                                )}

                                {/* mixed pairs */}
                                {evalLimitReached && memLimitReached && !tokenLimitReached && (
                                  <>You have used all {FREE_EVAL_LIMIT} free evaluations and all {FREE_MEM_LIMIT} free memory.</>
                                )}

                                {evalLimitReached && tokenLimitReached && !memLimitReached && (
                                  <>You have used all {FREE_EVAL_LIMIT} free evaluations and all {FREE_TOKEN_LIMIT} free tokens.</>
                                )}

                                {!evalLimitReached && memLimitReached && tokenLimitReached && (
                                  <>You have used all {FREE_MEM_LIMIT} free memory and all {FREE_TOKEN_LIMIT} free tokens.</>
                                )}

                                {" "}Upgrade to Pro to unlock unlimited runs and premium hardware access.
                              </div>

                              <Button className="eval-upgrade-btn" onClick={handleUpgrade}>
                                Upgrade Plan
                              </Button>
                            </div>
                          ) : (
                            <>
                              {/* ✅ ORANGE WARNINGS (show one or both) */}
                              {showEvalWarning && (
                                <div className="eval-warning-banner">
                                  <span className="eval-warning-dot">!</span>
                                  <span>
                                    You&apos;re almost out of free evaluations, only{" "}
                                    <b>{evalRemaining}</b> run{evalRemaining === 1 ? "" : "s"} left. Upgrade to keep running evaluations without interruption.
                                  </span>
                                </div>
                              )}

                              {showMemWarning && (
                                <div className="eval-warning-banner">
                                  <span className="eval-warning-dot">!</span>
                                  <span>
                                    You&apos;re almost out of free memory, only{" "}
                                    <b>{memRemainingFormatted}</b> left. Upgrade to keep running evaluations without interruption.
                                  </span>
                                </div>
                              )}
                              {showTokenWarning && (
                                <div className="eval-warning-banner">
                                  <span className="eval-warning-dot">!</span>
                                  <span>
                                    You&apos;re almost out of free tokens, only{" "}
                                    <b>{tokensRemainingFormatted}</b> left. Upgrade to keep running evaluations without interruption.
                                  </span>
                                </div>
                              )}

                              <LlmEvaluationField
                                key={LLM_DRAFT_SESSION_ID}
                                data={data}
                                onEvaluationCreated={(prompt, evalId, chatImage) => {
                                  setPendingCreatedEvaluationId(evalId);
                                  ensureSession(evalId);
                                  setChatBoxPrompt(prompt);
                                  setChatBoxPromptid(evalId);
                                  setchatImg(chatImage);

                                  setCallStart(true);
                                  setChatBox(true);
                                  setShowLogsPane(false);
                                  setCollapse(false);
                                }}
                                eval_id={null}
                                sessionKey={LLM_DRAFT_SESSION_ID}
                                session={sessions[LLM_DRAFT_SESSION_ID] || createEmptyLlmSession()}
                                updateSession={updateLLMSession}
                                message=""
                                modelName={data?.info?.name}
                                chipset={chipset}
                                compression={compression}
                                latestChipset={selectedLatestChipset}
                                latestDevice={selectedLatestDevice}
                                setLoadButton={setLoadButton}
                                loadButton={loadButton}
                                deviceStatusMap={
                                  Object.entries(latestDeviceStatus ?? {}).length > 0
                                    ? latestDeviceStatus
                                    : (data?.device_status ?? {})
                                }
                                evaluationStatusMap={{}}
                                busyPromptByEvalId={busyPromptByEvalId}
                                setBusyPromptByEvalId={setBusyPromptByEvalId}
                                tokensRemaining={tokensRemaining}
                              />
                            </>
                          )
                        ) : (
                          // <Button
                          //   color="primary"
                          //   className="evaluation-empty-btn py-3 px-4"
                          //   style={{ fontSize: '120%' }}
                          //   onClick={() => {
                          //     setOpenEvalDrawer(true);
                          //     setCollapse(true);
                          //     setCallStart(false);
                          //   }}
                          // >
                          //   <svg className="me-2" width="18" height="18" fill="none" stroke="white" strokeWidth="2"
                          //     viewBox="0 0 24 24">
                          //     <path strokeLinecap="round" strokeLinejoin="round"
                          //       d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
                          //   </svg>
                          //   Start Evaluation
                          // </Button>
                          <>
                            {limitReached && (
                              <div className="eval-limit-card">
                                <div className="eval-limit-icon">💳</div>
                                <div className="eval-limit-title">Evaluation Limit Reached</div>
                                <div className="eval-limit-subtitle">
                                  Your evaluation or memory allowance has been reached. Upgrade to Pro to unlock additional runs and premium hardware access.
                                </div>
                                <Button className="eval-upgrade-btn" onClick={handleUpgrade}>
                                  Upgrade Plan
                                </Button>
                              </div>
                            )}
                            {!limitReached && (
                            <>
                            {showEvalWarning && (
                              <div className="eval-warning-banner">
                                <span className="eval-warning-dot">!</span>
                                <span>You have only <b>{evalRemaining}</b> evaluation run{evalRemaining === 1 ? "" : "s"} remaining.</span>
                              </div>
                            )}
                            {showMemWarning && (
                              <div className="eval-warning-banner">
                                <span className="eval-warning-dot">!</span>
                                <span>You have only <b>{memRemainingFormatted}</b> of evaluation memory remaining.</span>
                              </div>
                            )}
                            <CvEvaluationField
                              key="hub-cv-evaluation-draft"
                              data={data}
                              embedded
                              onActiveSourceChange={setActiveEvaluationOption}
                              onEvaluationCreated={(result, selectedhw, imageslength) => {
                                setPendingCreatedEvaluationId(result?.evaluation_id || null);
                                setSelectedCvSampleImages([]);
                                setInitialEvaluationSource("");
                                setCallStart(true);
                                setCollapse(false);
                                settargethw(selectedhw);
                                setimageslength(imageslength);
                              }}
                              table_loading={load}
                              evalStatus={messagesByEvalId}
                              isTeriminated={isTeriminated}
                              chipset={chipset}
                              compression={compression}
                              setOpenEvalDrawer={setOpenEvalDrawer}
                              setLoadButton={setLoadButton}
                              loadButton={loadButton}
                              deviceStatusMap={
                                Object.entries(latestDeviceStatus ?? {}).length > 0
                                  ? latestDeviceStatus
                                  : (data?.device_status ?? {})
                              }
                              memRemainingMB={memRemainingMB}
                            />
                            </>
                            )}

                          {false && (
                          <div className="evaluation-cv-entry">
                            {limitReached ? (
                              // ✅ RED UPGRADE PANEL (if eval OR memory reached)
                              <div className="eval-limit-card">
                                <div className="eval-limit-icon">💳</div>

                                <div className="eval-limit-title">Evaluation Limit Reached</div>

                                <div className="eval-limit-subtitle">
                                  {evalLimitReached && memLimitReached && (
                                    <>You have used all {FREE_EVAL_LIMIT} free evaluations and all {FREE_MEM_LIMIT} free memory.</>
                                  )}
                                  {evalLimitReached && !memLimitReached && (
                                    <>You have used all {FREE_EVAL_LIMIT} free evaluations.</>
                                  )}
                                  {!evalLimitReached && memLimitReached && (
                                    <>You have used all {FREE_MEM_LIMIT} free memory.</>
                                  )}
                                  {" "}Upgrade to Pro to unlock unlimited runs and premium hardware access.
                                </div>

                                <Button className="eval-upgrade-btn" onClick={handleUpgrade}>
                                  Upgrade Plan
                                </Button>
                              </div>
                            ) : (
                              <>
                                {/* ✅ ORANGE WARNINGS (show one or both) */}
                                {showEvalWarning && (
                                  <div className="eval-warning-banner">
                                    <span className="eval-warning-dot">!</span>
                                    <span>
                                      You&apos;re almost out of free evaluations, only{" "}
                                      <b>{evalRemaining}</b> run{evalRemaining === 1 ? "" : "s"} left. Upgrade to keep running evaluations without interruption.
                                    </span>
                                  </div>
                                )}

                                {showMemWarning && (
                                  <div className="eval-warning-banner">
                                    <span className="eval-warning-dot">!</span>
                                    <span>
                                      You&apos;re almost out of free memory, only{" "}
                                      <b>{memRemainingFormatted}</b> left. Upgrade to keep running evaluations without interruption.
                                    </span>
                                  </div>
                                )}

                                <div className="evaluation-source-picker">
                                  <div className="evaluation-data-mode" aria-label="Test data type">
                                    <button
                                      type="button"
                                      className="evaluation-data-mode__option active"
                                      aria-pressed="true"
                                    >
                                      Custom Data
                                    </button>
                                    <button
                                      type="button"
                                      className="evaluation-data-mode__option evaluation-data-mode__option--disabled"
                                      disabled
                                      title="Benchmark datasets are coming soon"
                                    >
                                      <span>Benchmark Datasets</span>
                                      {/* <small>Coming soon</small> */}
                                    </button>
                                  </div>

                                  <div className="evaluation-source-picker__grid">
                                    {evaluationSources.map(({ source, title, description, icon, disabled }) => (
                                      <button
                                        key={source}
                                        type="button"
                                        className={`evaluation-source-card ${initialEvaluationSource === source ? "selected" : ""}`}
                                        disabled={disabled}
                                        onClick={() => handleEvaluationSourceSelect(source)}
                                        aria-pressed={initialEvaluationSource === source}
                                      >
                                        <span className="evaluation-source-card__icon">{icon}</span>
                                        <span className="evaluation-source-card__content">
                                          <strong>{title}</strong>
                                          <small>{description}</small>
                                        </span>
                                        {disabled ? (
                                          // <span className="evaluation-source-card__status">Coming soon</span>
                                          <></>
                                        ) : (
                                          <span className="evaluation-source-card__arrow" aria-hidden="true">→</span>
                                        )}
                                      </button>
                                    ))}
                                  </div>

                                  <div className="evaluation-source-picker__footer">
                                    {cvSampleImages.length > 0 && (
                                      <div className="evaluation-cv-samples">
                                        <span className="evaluation-cv-samples__label">
                                          Try with these sample <strong>Images</strong>.....
                                        </span>
                                        {data?.info?.task_type === "FaceRecognition" ? (
                                          <div className="evaluation-cv-samples__groups">
                                            {["reference", "test"].map((sampleType) => (
                                              <div
                                                key={sampleType}
                                                className={`evaluation-cv-sample-group evaluation-cv-sample-group--${sampleType}`}
                                              >
                                                <span className="evaluation-cv-sample-group__label">
                                                  {sampleType === "reference" ? "Reference Samples" : "Test Samples"}
                                                </span>
                                                <div className="evaluation-cv-samples__grid evaluation-cv-samples__grid--facenet">
                                                  {cvSampleImages
                                                    .filter((sample) => sample.target === sampleType)
                                                    .map((sample) => {
                                                      const sampleKey = `${sample.target}:${sample.name}`;
                                                      const isSelected = selectedCvSampleImages.some(
                                                        (item) => `${item.target}:${item.name}` === sampleKey
                                                      );

                                                      return (
                                                        <button
                                                          key={sampleKey}
                                                          type="button"
                                                          className={`evaluation-cv-sample ${isSelected ? "selected" : ""}`}
                                                          onClick={() => handleCvSampleSelect(sample)}
                                                          aria-pressed={isSelected}
                                                          title={`${isSelected ? "Remove" : "Select"} ${sample.label}`}
                                                        >
                                                          <img src={sample.src} alt={sample.label} loading="lazy" decoding="async" />
                                                        </button>
                                                      );
                                                    })}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="evaluation-cv-samples__grid">
                                            {cvSampleImages.map((sample) => {
                                              const sampleKey = `${sample.target}:${sample.name}`;
                                              const isSelected = selectedCvSampleImages.some(
                                                (item) => `${item.target}:${item.name}` === sampleKey
                                              );

                                              return (
                                                <button
                                                  key={sampleKey}
                                                  type="button"
                                                  className={`evaluation-cv-sample ${isSelected ? "selected" : ""}`}
                                                  onClick={() => handleCvSampleSelect(sample)}
                                                  aria-pressed={isSelected}
                                                  title={`${isSelected ? "Remove" : "Select"} ${sample.label}`}
                                                >
                                                  <img src={sample.src} alt={sample.label} loading="lazy" decoding="async" />
                                                </button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <div className="evaluation-source-picker__actions">
                                      <button
                                        type="button"
                                        className="evaluation-source-go"
                                        disabled={!selectedCvSampleImages.length}
                                        onClick={handleCvEvaluationGo}
                                      >
                                        <span>Go</span>
                                        <span className="evaluation-source-go__arrow" aria-hidden="true">
                                          <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                                            <path d="M2 7H12M8 3L12 7L8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                          )}
                          </>

                        )}
                      </div>
                    </section>
                      )}

                    </div>
                  </div>
                )}





                {/* 🟢 Evaluation Results view when user clicks a history card */}
                {childData?.eval_ids_names && evaluationSelect.length > 0 && (() => {
                  const selectedEval = evaluationSelect[0];
                  const selectedHistoryRow =
                    childData?.device_table?.data?.find(r => r.eval_id === selectedEval.value) || {};
                  const selectedRow = {
                    ...(evaluationDetailsById[selectedEval.value] || {}),
                    ...selectedHistoryRow,
                  };
                  const sectionStatus = selectedRow?.status || messagesByEvalId?.status;
                  const sectionEvalId = selectedEval?.value || messagesByEvalId?.evalId;
                  const selectedEvalId = messagesByEvalId?.evalId;
                  const selectedDataset =
                    selectedRow?.dataset || messagesByEvalId?.dataset || "";
                  const isVideoEvaluation = String(selectedDataset)
                    .toLowerCase()
                    .includes("video");

                  const processedImages =
                    totalImages?.[selectedEvalId]?.[1] || 0;

                  const canPause = processedImages >= 1;

                  // console.log('selectedRow', selectedRow)

                  const isCvEvalQueued = selectedRow?.status === "Queued";
                  const normalizedSectionStatus = String(sectionStatus || "Unknown").toLowerCase();
                  const sectionStatusLabel = normalizedSectionStatus === "unknown"
                    ? "Unknown"
                    : normalizedSectionStatus.charAt(0).toUpperCase() + normalizedSectionStatus.slice(1);
                  const historyChipset = String(selectedEval?.label || "")
                    .replace(/[_\s]+\d{4}-\d{2}-\d{2}.*$/, "")
                    .trim();
                  const evaluationChipset = String(
                    selectedRow?.chipset || historyChipset || ""
                  ).trim();
                  const rawEvaluationFamily = String(selectedRow?.family || "").trim();
                  const evaluationFamily = rawEvaluationFamily.toLowerCase() === "ambarella"
                    ? "Ambarella"
                    : rawEvaluationFamily;
                  const evaluationHardwareName = evaluationFamily &&
                    !evaluationChipset.toLowerCase().includes(evaluationFamily.toLowerCase())
                    ? `${evaluationFamily} ${evaluationChipset}`.trim()
                    : evaluationChipset || evaluationFamily || "Target hardware";
                  const evaluationUserName =
                    selectedRow?.user_name ||
                    selectedRow?.username ||
                    selectedRow?.created_by ||
                    localStorage.getItem("name") ||
                    data?.info?.created_by ||
                    "Unknown user";
                  const evaluationInitials = String(evaluationChipset || evaluationFamily || "E")
                    .split(/\s+|[-_]/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part.charAt(0))
                    .join("")
                    .toUpperCase();
                  const progressTotal = totalImages?.[sectionEvalId]?.[0] || 0;
                  const progressCompleted = Math.min(
                    completedCountByEvalId?.[sectionEvalId] ?? totalImages?.[sectionEvalId]?.[1] ?? 0,
                    progressTotal
                  );
                  const progressPercent = progressTotal > 0
                    ? Math.min(100, (progressCompleted / progressTotal) * 100)
                    : 0;
                  const executionTime = selectedRow?.status === "Queued"
                    ? <QueuedCountdown waitTime={selectedRow?.wait_time} />
                    : selectedRow?.status === "Evaluating"
                      ? TimeFormatter(selectedRow?.estimated_time)
                      : selectedRow?.status === "Paused"
                        ? TimeFormatter(selectedRow?.progress_time)
                        : ["Complete", "Terminated", "Failed"].includes(selectedRow?.status)
                          ? TimeFormatter(selectedRow?.completed_time)
                          : "—";
                  const compressionLabel = selectedRow?.compression != null && String(selectedRow.compression).trim() !== ""
                    ? String(selectedRow.compression).replace(/_/g, " ")
                    : "—";
                  const evaluationMetricDetails = InnerMetrics.map((metric) => ({
                    label: metric.title1(selectedRow),
                    value: metric.value(selectedRow, false),
                  }));
                  const accuracyMetric = evaluationMetricDetails[0];
                  const performanceMetricDetails = evaluationMetricDetails.slice(1);
                  const evaluationSummaryPanels = [
                    {
                      id: "target-hardware",
                      label: "Target hardware",
                      shortLabel: "Target HW",
                      shortValue: evaluationHardwareName,
                      title: evaluationHardwareName,
                      description: "Hardware selected to run this evaluation.",
                      details: [
                        ...(evaluationFamily
                          ? [{ label: "Family", value: evaluationFamily }]
                          : []),
                        { label: "Chipset", value: evaluationChipset || "—" },
                        // { label: "Device", value: selectedRow?.device || "—" },
                      ],
                      tone: "green",
                      icon: <ChipsetIcon className="wf-icon-small" />,
                    },
                    {
                      id: "input",
                      label: "Evaluation input",
                      shortLabel: "Input",
                      shortValue: selectedRow?.dataset || "—",
                      title: selectedRow?.dataset || "Input not available",
                      description: "Input source and resolution used for the evaluation.",
                      details: [
                        { label: "Input", value: selectedRow?.dataset || "—" },
                        { label: "Model Input Size", value: selectedRow?.input_size || "—" },
                      ],
                      tone: "blue",
                      icon: <DatasetIcon className="wf-icon-small" />,
                    },
                    {
                      id: "execution",
                      label: "Test Execution time",
                      shortLabel: "Execution",
                      shortValue: executionTime,
                      title: executionTime,
                      description: "Run timing and current evaluation state.",
                      details: [
                        // { label: "Status", value: sectionStatusLabel },
                        { label: "Execution time", value: executionTime },
                      ],
                      tone: "amber",
                      icon: <RoundedClockIcon className="wf-icon-small" />,
                    },
                    {
                      id: "accuracy",
                      label: " Model Accuracy",
                      shortLabel: "Accuracy",
                      shortValue: accuracyMetric?.value || "—",
                      title: "Accuracy",
                      description: "Accuracy measured for this evaluation.",
                      details: [
                        {
                          label: accuracyMetric?.label || "Metric",
                          value: accuracyMetric?.value || "—",
                        },
                      ],
                      tone: "violet",
                      icon: <AccuracyIcon2 className="wf-icon-small" />,
                    },
                    {
                      id: "metrics",
                      label: "Performance",
                      shortLabel: "Performance",
                      shortValue: performanceMetricDetails[0]?.value || "—",
                      title: "Performance overview",
                      description: "Measured performance for this evaluation run.",
                      details: [
                        { label: "Compression", value: compressionLabel },
                        performanceMetricDetails[1],
                        performanceMetricDetails[2],
                        performanceMetricDetails[0],
                      ],
                      tone: "red",
                      icon: <RunningIcon className="wf-icon-small" />,
                    },
                  ];

                  return (
                    <div ref={resultsPageRef} className={`evaluation-results-page p-0 d-flex flex-column${isLLMTaskType ? " evaluation-results-page--llm" : " evaluation-results-page--cv"}${isCompactLayout ? " evaluation-results-page--compact bg-transparent" : isLLMTaskType ? " container-card bg-transparent" : ""}`} style={{ height: "100%", minHeight: 0, overflow: "hidden" }}>

                      {renderCompactCanvasToolbar()}

                      {!isLLMTaskType && (
                      <div className="evaluation-detail-shell evaluation-results-summary-stack d-flex flex-column" style={{ flexShrink: 0 }}>
                        <div className="container-card evaluation-results-identity-card">
                          <div className="evaluation-results-identity-header">
                            <div className="evaluation-results-identity">
                              <span className="evaluation-results-identity__avatar" aria-hidden="true">
                                {evaluationInitials || "E"}
                              </span>
                              <div className="evaluation-results-identity__copy">
                                <div className="evaluation-results-identity__title-row">
                                  <h2 title={evaluationHardwareName}>{evaluationHardwareName}</h2>
                                  <span className={`evaluation-results-status evaluation-results-status-${normalizedSectionStatus}`}>
                                    {sectionStatusLabel}
                                  </span>
                                </div>
                                <div className="evaluation-results-identity__meta">
                                  <span className="evaluation-results-identity__user" title={evaluationUserName}>
                                    <img src="/ico/user.png" width={15} height={15} alt="" aria-hidden="true" />
                                    {evaluationUserName}
                                  </span>
                                  {selectedRow?.created_time && (
                                    <>
                                      <div className="vertical-divider" style={{ height: "10px" }} />
                                      <span className="d-j-a gap-1" style={{ width: "fit-content" }}>
                                        Created {DateParse(selectedRow.created_time)}&nbsp;
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="dataset-detail-header-actions d-flex align-items-start">
                              {!['complete', 'terminated', 'failed', 'queued'].includes(normalizedSectionStatus) && (
                                  <div className="evaluation-results-running-actions d-a gap-2">
                                    <button
                                      className={`rounded px-2 ${!isPausedStatus ? "wf-icon-yellow" : "wf-icon-green"}`}
                                      disabled={
                                        callStart ||
                                        (!isPausedStatus ? !canPauseWithData : !canResume)
                                      }
                                      style={{
                                        padding: "3px",
                                        border: !isPausedStatus ? "1px solid orange" : "1px solid green",
                                        whiteSpace: "nowrap",
                                        width: "fit-content",
                                        height: "unset",
                                        opacity:
                                          (!isPausedStatus ? !canPauseWithData : !canResume) ? 0.5 : 1,
                                        cursor:
                                          (!isPausedStatus ? !canPauseWithData : !canResume)
                                            ? "not-allowed"
                                            : "pointer",
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (!isPausedStatus && !canPauseWithData) return;
                                        if (isPausedStatus && !canResume) return;
                                        handlePauseResume();
                                      }}
                                    >
                                      {callStart ? (
                                        <FontAwesomeIcon
                                          icon={faSpinner}
                                          spin
                                          color={!isPausedStatus ? "orange" : "green"}
                                          style={{ fontSize: "90%" }}
                                        />
                                      ) : (
                                        <FontAwesomeIcon
                                          icon={!isPausedStatus ? faPause : faPlay}
                                          color={!isPausedStatus ? "orange" : "green"}
                                          style={{ fontSize: "90%" }}
                                        />
                                      )}
                                      &nbsp;{!isPausedStatus ? "Pause" : "Resume"}
                                    </button>

                                    <button
                                      className="wf-icon-red rounded px-2"
                                      disabled={callStartTerminate || !canTerminate}
                                      style={{
                                        padding: "3px",
                                        border: "1px solid red",
                                        whiteSpace: "nowrap",
                                        width: "fit-content",
                                        height: "unset",
                                        opacity: !canTerminate ? 0.5 : 1,
                                        cursor: !canTerminate ? "not-allowed" : "pointer",
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (!canTerminate) return;
                                        handleActions("Terminate");
                                      }}
                                    >
                                      {callStartTerminate ? (
                                        <FontAwesomeIcon
                                          icon={faSpinner}
                                          spin
                                          style={{ fontSize: "90%" }}
                                          color="red"
                                        />
                                      ) : (
                                        <FontAwesomeIcon
                                          icon={faStop}
                                          style={{ fontSize: "90%" }}
                                          color="red"
                                        />
                                      )}
                                      &nbsp;Stop
                                    </button>
                                  </div>
                                )}
                            </div>
                          </div>

                          {normalizedSectionStatus === "evaluating" && (
                            <div className="evaluation-results-progress-card">
                              <div className="evaluation-results-progress-card__top">
                                <span>Progress ({progressCompleted}/{progressTotal || 0} inputs)</span>
                                <span>{Math.round(progressPercent)}%</span>
                              </div>
                              <div
                                className="evaluation-results-progress-card__bar"
                                style={{ "--evaluation-progress": `${progressPercent}%` }}
                              >
                                <span style={{ width: `${progressPercent}%` }} />
                              </div>
                              <div className="evaluation-results-progress-card__bottom">
                                <span>Running time {TimeFormatter(selectedRow?.progress_time || smoothTime)}</span>
                                <span>Estimated completion {TimeFormatter(selectedRow?.estimated_time)}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <EvaluationSummaryStrip
                          panels={evaluationSummaryPanels}
                          evaluationId={sectionEvalId}
                        />
                      </div>
                      )}

                        {!isLLMTaskType && data && !isCvEvalQueued && (
                          <div
                            className="d-flex align-items-center overflow-auto gap-2 py-2 px-2 border-end-md-0 datset-verssion-detail-content-tab-bar container-card evaluation-detail-tab-bar"
                            style={{
                              borderLeftColor: "var(--leading-color)",
                              borderLeftWidth: "3px",
                              borderRadius: "7px",
                              scrollbarWidth: "none",
                            }}
                          >
                            <button
                              type="button"
                              className={`d-a gap-1 plain-button2 text-nowrap px-3 py-1 fw-normal ${showTab === "preview" ? "bg-main rounded text-white" : "text"}`}
                              style={{ fontSize: "90%" }}
                              onClick={() => setShowTab("preview")}
                            >
                              <ImagesIcon className="wf-icon-small" style={{ padding: "2px" }} />
                              <span style={{ lineHeight: "normal" }}>Prediction Preview</span>
                            </button>

                            <button
                              type="button"
                              className={`d-a gap-1 plain-button2 text-nowrap px-3 py-1 fw-normal ${showTab === "analytics" ? "bg-main rounded text-white" : "text"}`}
                              style={{ fontSize: "90%" }}
                              onClick={() => setShowTab("analytics")}
                            >
                              <AnalyticsIcon className="wf-icon-small" />
                              <span style={{ lineHeight: "normal" }}>Prediction Analytics</span>
                            </button>

                            <button
                              type="button"
                              className={`d-a gap-1 plain-button2 text-nowrap px-3 py-1 fw-normal ${showTab === "logs" ? "bg-main rounded text-white" : "text"}`}
                              style={{ fontSize: "90%" }}
                              onClick={() => setShowTab("logs")}
                            >
                              <CodeIcon className="wf-icon-small" />
                              <span style={{ lineHeight: "normal" }}>Evaluation Logs</span>
                            </button>

                            {isSelectedEvaluationComplete && (
                              <button
                                type="button"
                                className={`d-a gap-1 plain-button2 text-nowrap px-3 py-1 fw-normal ms-auto ${showTab === "insights" ? "bg-main rounded text-white" : "text"}`}
                                style={{ fontSize: "90%" }}
                                onClick={() => setShowTab("insights")}
                              >
                                <CustomisePlanIcon className="wf-icon-small" />
                                <span style={{ lineHeight: "normal" }}>AI Insights</span>
                              </button>
                            )}
                          </div>
                        )}

                        {!isLLMTaskType && data && !isCvEvalQueued && (
                          <EvaluationProgressStream
                            evalId={sectionEvalId}
                            status={sectionStatusLabel}
                            onCount={(count, total) => {
                              if (!sectionEvalId) return;

                              setCompletedCountByEvalId((prev) => {
                                const previous = prev[sectionEvalId] || 0;
                                return {
                                  ...prev,
                                  [sectionEvalId]: Math.max(previous, count),
                                };
                              });

                              if (total) {
                                setTotalImages((prev) => {
                                  const existing = prev[sectionEvalId];

                                  return {
                                    ...prev,
                                    [sectionEvalId]: [
                                      total,
                                      Math.max(existing?.[1] || 0, count),
                                    ],
                                  };
                                });
                              }
                            }}
                            onDone={() => {
                              const total = totalImages?.[sectionEvalId]?.[0];

                              if (sectionEvalId && total) {
                                setCompletedCountByEvalId((prev) => ({
                                  ...prev,
                                  [sectionEvalId]: total,
                                }));
                              }
                            }}
                          />
                        )}




                      <div
                        ref={scrollContainerRef}
                        className={isLLMTaskType ? "evaluation-results-llm-body evaluation-results-llm-stage" : "evaluation-detail-panel"}
                        style={isLLMTaskType ? { minHeight: 0, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column" } : { minHeight: 0, overflow: "hidden" }}
                      >
                        {(selectedRow?.status || "").charAt(0).toUpperCase() + (selectedRow?.status || "").slice(1) === "Queued" ? (
                          /* 🚦 QUEUED STATE */
                          <div className="d-j-a flex-column h-100 text-center">

                            <div className="queue-icon-status-wrapper mb-3">
                              <QueueChipIcon className="wf-icon-blue" />
                              <span className="queue-status-dot" />
                            </div>



                            <h6 className="mb-2">Evaluation is queued</h6>

                            <p className="text-muted mb-3">
                              Job <strong>{selectedRow?.name}</strong> is pending.
                              <br />
                              Resources are being allocated on the{" "}
                              <strong className="text-uppercase">{selectedRow?.chipset}</strong> device.
                            </p>


                            <p className="text-muted mb-3">
                              {/* Your evaluation is waiting for available resources. */}
                              It will start automatically once a device is free.
                            </p>

                            <div
                              className="d-a p-2 px-3 evaluation-second-history-card-active container-card m-auto my-3 text-primary"
                              style={{ width: "fit-content" }}
                            >
                              <RoundedClockIcon className="wf-icon-small text-primary" />&nbsp;
                              <span>
                                EST. WAIT:&nbsp;
                                {TimeFormatter(smoothTime)}

                              </span>
                            </div>

                            <div className="d-none">
                              <LlmEvaluationField
                                key={messagesByEvalId?.evalId || chatBoxPromptid}
                                data={data}
                                onEvaluationCreated={() => setCallStart(true)}
                                eval_id={messagesByEvalId?.evalId}
                                chatBox={chatBox}
                                sessionKey={messagesByEvalId?.evalId}
                                session={sessions[messagesByEvalId?.evalId] || createEmptyLlmSession()}
                                updateSession={updateLLMSession}
                                collapse={collapse}
                                message={messagesByEvalId.status}
                                selectedDeviceInfo={selectedRow?.chipset}
                                chatBoxPrompt={chatBoxPrompt}
                                setChatBoxPrompt={setChatBoxPrompt}
                                chatBoxPromptid={chatBoxPromptid}
                                setChatBoxPromptid={setChatBoxPromptid}
                                modelName={data?.info?.name}
                                evalStatus={message}
                                chatImag={chatImag}
                                setchatImg={setchatImg}
                                chipset={chipset}
                                compression={compression}
                                latestChipset={selectedLatestChipset}
                                latestDevice={selectedLatestDevice}
                                evaluationStatusMap={messagesByEvalId}
                                busyPromptByEvalId={busyPromptByEvalId}
                                setBusyPromptByEvalId={setBusyPromptByEvalId}
                                tokensRemaining={tokensRemaining}
                              />
                            </div>




                            {/* <div className="d-a justify-content-center gap-2">
                              <span role="img" aria-label="clock">⏳</span>
                              <span>Please wait…</span>
                            </div> */}
                          </div>
                        ) : (data && isLLMTaskType ? (
                          <div className="evaluation-results-llm-view h-100">
                            {showLogsPane && messagesByEvalId?.evalId ? (
                              <>
                                <div className="evaluation-results-llm-alternate-content">
                                  <div className="evaluation-results-logs-panel evaluation-results-logs-panel--page">
                                    <Logs
                                      key={`logs-${messagesByEvalId?.evalId}`}
                                      type={"models"}
                                      EvalUrl={`evaluations?filters={"_id":"${messagesByEvalId?.evalId}"}&section=logs`}
                                      hub={true}
                                      message={messagesByEvalId?.status}
                                      selectedAction={"Logs"}
                                      full={true}
                                      eval_id={messagesByEvalId?.evalId}
                                    />
                                  </div>
                                </div>
                              </>
                            ) : messagesByEvalId?.evalId ? (
                              <LlmEvaluationField
                                key={messagesByEvalId?.evalId || chatBoxPromptid}
                                data={data}
                                onEvaluationCreated={() => setCallStart(true)}
                                eval_id={messagesByEvalId?.evalId}
                                chatBox={chatBox}
                                useGlobalScroll
                                sessionKey={messagesByEvalId?.evalId}
                                session={sessions[messagesByEvalId?.evalId] || createEmptyLlmSession()}
                                updateSession={updateLLMSession}
                                collapse={collapse}
                                message={messagesByEvalId.status}
                                chatBoxPrompt={chatBoxPrompt}
                                setChatBoxPrompt={setChatBoxPrompt}
                                chatBoxPromptid={chatBoxPromptid}
                                setChatBoxPromptid={setChatBoxPromptid}
                                modelName={data?.info?.name}
                                selectedDeviceInfo={selectedRow?.chipset}
                                evalStatus={message}
                                chatImag={chatImag}
                                setchatImg={setchatImg}
                                chipset={chipset}
                                compression={compression}
                                latestChipset={selectedLatestChipset}
                                latestDevice={selectedLatestDevice}
                                evaluationStatusMap={messagesByEvalId}
                                busyPromptByEvalId={busyPromptByEvalId}
                                setBusyPromptByEvalId={setBusyPromptByEvalId}
                                tokensRemaining={tokensRemaining}
                              />
                            ) : null}
                          </div>
                        ) : (
                          <>
                            <div className={`evaluation-detail-content px-0 ${showTab === "analytics" ? "evaluation-detail-content--analytics" : ""}`} style={{ height: "100%", minHeight: 0, scrollbarWidth: "thin" }}>


                              {/* Prediction Preview */}
                              {showTab === "preview" && (
                                <>
                                  <div className="evaluation-detail-preview w-100">
                                {messagesByEvalId?.dataset !== "Stream" && (
                                  <Samplepredictionstream
                                    compactHeader
                                    hideTitle
                                    key={`preview-${sectionEvalId}`}
                                    keyData={`(${devicestatus ? devicestatus : messagesByEvalId?.status})-${messagesByEvalId?.evalId}`}
                                    type={isVideoEvaluation ? "video" : "models"}
                                    samUrl={samUrl}
                                    message={sectionStatus}
                                    targetHW={selectedTarget}
                                    reslt={true}
                                    id={sectionEvalId}
                                    device={evaluationSelect[0]?.label}
                                    modelName={data?.info?.name}
                                    task_type={data?.info?.task_type}
                                    streamData={streamData}
                                    setStreamData={setStreamData}
                                    targethw={selectedRow?.chipset}
                                    imageslength={imageslength}
                                    llmReportAvailable={selectedRow?.llm_report === true}
                                    isdeviceselected={isdeviceselected}
                                    collapse={collapse}
                                    // onEvaluationCreated={(totalImages, fetchedImages) => {
                                    //   setTotalImages((prev) => ({
                                    //     ...prev,
                                    //     [messagesByEvalId?.evalId]: [totalImages, fetchedImages],
                                    //   }));
                                    //   setTotalImages2((prev) => ({
                                    //     ...prev,
                                    //     [messagesByEvalId?.evalId]: [totalImages, fetchedImages],
                                    //   }));

                                    // }}
                                    completedCount={completedCountByEvalId?.[sectionEvalId] || 0}
                                  />
                                )}
                                  </div>
                                </>
                              )}

                              {/* Analytics row */}

                              {showTab === "analytics" && (
                                <div className="evaluation-detail-analytics w-100">
                                  <Plots
                                    key={`analytics-${sectionEvalId}`}
                                    evaluation_id={sectionEvalId}
                                    EvalUrl={`evaluations?filters={"_id":"${sectionEvalId}"}&section=analytics`}
                                    hub={true}
                                    message={sectionStatus}
                                    modelName={data?.info?.name}
                                    task_type={data?.info?.task_type}
                                    live={sectionStatus === "Evaluating"}
                                    cache={cachedEval.plots}
                                    setCache={(data) => {
                                      if (!evaluationId) return;
                                      evaluationCacheRef.current[evaluationId] = {
                                        ...evaluationCacheRef.current[evaluationId],
                                        plots: data
                                      };
                                    }}

                                  />
                                </div>
                              )}

                              {/* AI Insights */}

                              {showTab === "insights" && isSelectedEvaluationComplete && (
                                <div className="evaluation-detail-insights w-100">
                                <AiInsights
                                  evaluationId={messagesByEvalId?.evalId}
                                  status={messagesByEvalId?.status}
                                  llmReportAvailable={selectedRow?.llm_report === true}
                                />
                                </div>
                              )}

                              {/* Logs */}

                              {showTab === "logs" && (
                                <div className="evaluation-detail-logs w-100">
                                <Logs
                                  // key={`logs-${messagesByEvalId?.evalId}`}
                                  // key={`(${devicestatus ? devicestatus : messagesByEvalId?.status})-${messagesByEvalId?.evalId}`}
                                  key={`logs-${sectionEvalId}`}
                                  type={"models"}
                                  EvalUrl={`evaluations?filters={"_id":"${sectionEvalId}"}&section=logs`}
                                  hub={true}
                                  message={sectionStatus}
                                  selectedAction={"Logs"}
                                  eval_id={sectionEvalId}
                                  hideTitle
                                />
                                </div>
                              )}
                            </div>
                          </>
                        ))}
                      </div>

                    </div>
                  );
                })()}


              </div>
            </div>
          </div>

          <div className="mobile-view-bottom"></div>
        </>
      ) : (
        <Loader />
      )}

      <Modal
        className=""
        isOpen={openModal}
        onRequestClose={closeModal}
        contentLabel="Example Modal"
      >
        <Button
          className="float-right"
          color="transparant"
          onClick={closeModal}
          style={{
            position: "sticky",
            zIndex: 9999,
            top: 0,
            marginRight: "-8px",
          }}
        >
          <FontAwesomeIcon icon={faTimes} color="grey" size="lg" />
        </Button>
        <Deploy />
      </Modal>
      <ModalComponent
        modalIsOpen={model}
        setModalIsOpen={setModel}
        modalClass={"bg-white p-3"}
        component={"Component"}
      />
      {/* {deleteUrl && <Confirm url={deleteUrl} setDeletingEvalId={setDeletingEvalId} close={() => {setDeleteUrl(); setDeletingEvalId(null);}} onDeleteDone={(isDeleted) => {
        // console.log(isDeleted)
        // console.log('first page makign', childData?.eval_ids_names?.length)
        // setCallStart(true)

        if (!isDeleted) {
          // setDeletingEvalId(null);
          return;
        }

        setEvaluationSelect([]);
        // setDeletingEvalId(null);
        // setDeleteUrl(undefined);

        // const deletedId = deletedEvalIdRef.current;
        // const existsInChild =
        //   !!childData?.eval_ids_names?.some(x => x?.value === deletedId);

        // // If the deleted id is in childData and we’re not already on page 1,
        // // force the EvaluationTablestream to open on page 2 once.
        // if (childData?.eval_ids_names?.length == 1 && page !== 1) {
        //   // console.log('first page makign ', page - 1)
        //   setpageChange(page - 1);
        // } else if (childData?.eval_ids_names?.length == 1 && page === 1) {
        //   // console.log('first page makign -5', childData?.eval_ids_names?.length)
        //   setpageChange('-5');
        //   setdetails("Model info")
        // } else {
        //   setpageChange(page);
        // }


        // selectNextAfterDelete();
      }}
      />} */}


      {deleteUrl && (
        <Confirm
          url={deleteUrl}
          setDeletingEvalId={setDeletingEvalId}
          close={() => {
            setDeleteUrl(undefined);
            deletingEvalIdRef.current = null;
            setDeletingEvalId();

          }}
          onDeleteDone={(isDeleted) => {
            if (!isDeleted) return;

            const deletedId = deletingEvalIdRef.current;
            if (!deletedId) return;


            setEvaluationSelect([]);

            if (childData?.eval_ids_names?.length === 1) {
              // ✅ REMOVE FROM childData (this fixes left panel)
              setChildData((prev) => {
                if (!prev) return prev;

                return {
                  ...prev,
                  eval_ids_names: prev.eval_ids_names.filter(
                    (e) => e.value !== deletedId
                  ),
                  device_table: {
                    ...prev.device_table,
                    data: prev.device_table.data.filter(
                      (row) => row.eval_id !== deletedId
                    ),
                  },
                };
              });

              // clear selection if deleted one was selected
              if (evaluationSelect?.[0]?.value === deletedId) {
                setEvaluationSelect([]);
              }

              // cleanup
              // setDeleteUrl(undefined);
              deletingEvalIdRef.current = null;
            }
          }}
        />
      )}






      <div className={`eval-right-drawer-overlay ${!openEvalDrawer && 'd-none'}`} onClick={() => { setOpenEvalDrawer(false); setLoadButton(false) }}>
        <div
          className="eval-right-drawer-panel p-0"
          onClick={(e) => e.stopPropagation()} // prevent close on inside click
        >
          <CvEvaluationField
            key={`project-evaluation-${initialEvaluationSource || "default"}-${openEvalDrawer ? "open" : "closed"}`}
            ref={rendertextfieldref}
            data={data}
            initialDataSource={initialEvaluationSource}
            initialSuggestedImages={selectedCvSampleImages}
            hideTestDataSelection
            onEvaluationCreated={(result, selectedhw, imageslength, selectedTool) => {
              setPendingCreatedEvaluationId(result?.evaluation_id || null);
              setSelectedCvSampleImages([]);
              setInitialEvaluationSource("");
              setOpenEvalDrawer(false);
              setCallStart(true);
              setCollapse(false);
              settargethw(selectedhw);
              setimageslength(imageslength);
            }}
            table_loading={load}
            evalStatus={messagesByEvalId}
            // onSelectionChange={handleSelectionChange}
            isTeriminated={isTeriminated}
            chipset={chipset}
            compression={compression}
            setOpenEvalDrawer={setOpenEvalDrawer}
            setLoadButton={setLoadButton}
            loadButton={loadButton}
            deviceStatusMap={
              Object.entries(latestDeviceStatus ?? {}).length > 0
                ? latestDeviceStatus
                : (data?.device_status ?? {})
            }
            memRemainingMB={memRemainingMB}
          />
        </div>
      </div>



      <Modal
        isOpen={showModelInfoModal}
        onRequestClose={() => setShowModelInfoModal(false)}
        className="evaluation-model-info-modal"
        overlayClassName="evaluation-model-info-modal-overlay"
        contentLabel="Model information"
      >
        <div className="evaluation-model-info-modal__header">
          <div>
            <span className="evaluation-model-info-modal__eyebrow">Model information</span>
            <h4>{uiModelName}</h4>
          </div>
          <button
            type="button"
            className="evaluation-model-info-modal__close"
            onClick={() => setShowModelInfoModal(false)}
            aria-label="Close model information"
          >
            <CloseIcon className="wf-icon text-secondary-small" />
          </button>
        </div>

        <div className="evaluation-model-info-modal__meta">
          <span className={data?.info?.access === "private" ? "status-tool-red" : "status-tool-green"}>
            {data?.info?.access || "—"}
          </span>
          {data?.info?.created_on && (
            <span className="d-a gap-1 text-muted">
              <RoundedClockIcon className="wf-icon-small" />
              Published {DateParse(data.info.created_on)}
            </span>
          )}
        </div>

        <div className="evaluation-model-info-modal__grid">
          <div>
            <span>Framework</span>
            <strong className="text-capitalize">
              {data?.info?.framework === "pytorch" ? "PyTorch" : data?.info?.framework || "—"}
            </strong>
          </div>
          <div>
            <span>Task</span>
            <strong>{uiTaskType || "—"}</strong>
          </div>
          <div>
            <span>Model size</span>
            <strong>
              {summary?.total?.TotalModelMemoryUsageMB != null
                ? `${summary.total.TotalModelMemoryUsageMB} MB`
                : "—"}
            </strong>
          </div>
          <div>
            <span>Total parameters</span>
            <strong>{summary?.total?.TotalParams ?? "—"}</strong>
          </div>
          {fromProjects && data?.info?.created_by && (
            <div>
              <span>Created by</span>
              <strong>{data.info.created_by}</strong>
            </div>
          )}
        </div>
      </Modal>


      <Modal
        isOpen={showLlmMetricsPanel && isLLMTaskType}
        onRequestClose={() => setShowLlmMetricsPanel(false)}
        className="layer-modal evaluation-llm-metrics-modal"
        overlayClassName="layer-modal-overlay"
        contentLabel="Evaluation metrics"
      >
        <div className="layer-modal-header">
          <div>
            <span className="evaluation-model-info-modal__eyebrow">Evaluation results</span>
            <h4>Evaluation Metrics</h4>
          </div>
          <button
            type="button"
            className="evaluation-model-info-modal__close"
            onClick={() => setShowLlmMetricsPanel(false)}
            aria-label="Close evaluation metrics"
          >
            <CloseIcon className="wf-icon text-secondary-small" />
          </button>
        </div>

        <div className="layer-modal-content evaluation-llm-metrics-modal__content">
          {renderLlmMetricsPanel(selectedRow)}
        </div>
      </Modal>



      <Modal
        isOpen={showLayerModal}
        onRequestClose={() => setShowLayerModal(false)}
        className="layer-modal"
        overlayClassName="layer-modal-overlay"
        style={{ minHeight: "525px" }}
      >
        <div className="layer-modal-header">
          <h4>
            {layerModalType === "summary" ? "Model Layer Summary" : "Model Layer Compatibility"}
          </h4>

          <div className="d-a gap-2">
            {layerModalType === "summary" ? (
              summary && <div className="d-a rounded badge-border">
                <Select
                  className="my-select layer-modal-select"
                  classNamePrefix="my-select"
                  styles={customStyles2}
                  formatOptionLabel={formatOptionLabel}
                  value={summaryfilter || null}
                  placeholder="Select Operations ..."
                  onChange={(e) => {
                    if (e === null) {
                      setSummaryfilter(null);
                      modelSummary(); // clear logic
                    } else {
                      setSummaryfilter(e);
                    }
                  }}
                  options={
                    summary?.filters
                      ? summary.filters.map((chip) => ({
                        value: chip,
                        label: chip,
                      }))
                      : []
                  }
                  hideSelectedOptions={false}
                  isClearable
                />
              </div>
            )
              :
              (
                <div className="d-a gap-2">
                  <div className="rounded badge-border">
                    <Select
                      className="my-select layer-modal-select"
                      classNamePrefix="my-select"
                      styles={customStyles2}
                      formatOptionLabel={formatOptionLabel}
                      value={Compatibilytfilterfamily || null}
                      placeholder="Select family ..."
                      onChange={(e) => {
                        if (e === null) {
                          // User clicked the x to clear
                          setCompatibilityfilterfamily(null);
                          setCompatibilityfilterchipset(null);
                          modelCompatibility(); // call your clear logic
                        } else {
                          console.log("eee", e)
                          setCompatibilityfilterfamily(e);
                          setCompatibilityfilterchipset(""); // reset chipset when family changes
                        }
                      }}
                      options={filterOptions}
                      hideSelectedOptions={false}
                      isClearable
                    />
                  </div>
                  <div className="rounded badge-border">
                    <Select
                      className="my-select layer-modal-select"
                      classNamePrefix="my-select"
                      styles={customStyles2}
                      formatOptionLabel={formatOptionLabel}
                      value={
                        Compatibilytfilterchipset
                          ? {
                            value: Compatibilytfilterchipset,
                            label: Compatibilytfilterchipset,
                            type: "chipset",
                          }
                          : null
                      }
                      placeholder="Select Chipset"
                      onChange={(e) => {
                        if (e === null) {
                          setCompatibilityfilterchipset(null);
                          modelCompatibility(); // or other clear logic
                        } else {
                          setCompatibilityfilterchipset(e.value);
                        }
                      }}
                      options={
                        Compatibilytfilterfamily
                          ? compatibilityfilters[
                            Compatibilytfilterfamily?.value
                          ]?.map((chip) => ({
                            value: chip,
                            label: chip,
                            type: "chipset"
                          }))
                          : []
                      }
                      isDisabled={!Compatibilytfilterfamily}
                      hideSelectedOptions={false}
                      isClearable
                    />
                  </div>
                </div>
              )
            }

            <button className="close-btn" onClick={() => {
              setShowLayerModal(false);
              modelSummary();
              modelCompatibility();
            }
            }>
              <CloseIcon className="wf-icon text-secondary" />
            </button>
          </div>
        </div>

        <div className="layer-modal-content">
          {layerModalType === "summary" && (
            <ModalSummary2 data={summary?.summary} />
          )}

          {layerModalType === "compatibility" && (
            <ModalSummary2 data={compatibilitydata} id={data?.info?._id} />
          )}
        </div>
      </Modal>



    </>
  );
}

function ModalSummary2({ data, selectedFilter, id }) {
  const [tableData, setTableData] = React.useState({ columns: [], data: [] });

  React.useEffect(() => {
    if (!data) return;

    let temp = data?.data || [];

    if (selectedFilter?.length > 0) {
      try {
        if (temp[0]?.className) {
          temp = temp.filter((v) => selectedFilter.includes(v.className));
        } else {
          temp = temp.filter((v) =>
            selectedFilter.includes(v.incompatibleDevices)
          );
        }
      } catch (error) {
        // console.log(error);
      }
    }

    setTableData({ columns: data?.columns || [], data: temp });
  }, [selectedFilter, data]);

  return (
    <div className="mt-0 evaluation-table table-default w-100 table-overflow">
      {data && (
        <Table
          key={id}
          columns={tableData.columns}
          data={tableData.data}
          rows={7}
          model_comp={id}
          layerdata={true}
        />
      )}
    </div>
  );
}

function QueuedCountdown({ waitTime }) {
  const [remaining, setRemaining] = React.useState(null);

  React.useEffect(() => {
    if (!waitTime || waitTime === "NA") return;

    const parseUTC = (timeString) =>
      new Date(
        timeString.replace(" ", "T").split(".")[0] + "Z"
      ).getTime();

    const end = parseUTC(waitTime);

    const update = () => {
      const now = Date.now();
      const diff = (end - now) / 1000;

      if (diff <= 1) {
        setRemaining("ANY_TIME");
      } else {
        setRemaining(Math.ceil(diff)); // 🔥 use ceil for smoother UX
      }
    };

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, [waitTime]);

  if (remaining === "ANY_TIME") {
    return <>Any time</>;
  }

  if (remaining == null) {
    return null;
  }

  return <>{TimeFormatter(remaining)}</>;
}
